// groundmat — GROUND-M runtime ground materials (APP_SURFACE.md §Ground
// materials). The bake carries absolute colour + light at low frequency;
// this adds everything finer than ~3 m: a mean-1 albedo multiplier and a
// detail normal per surface class, from client/materials (make-materials.mjs).
// It replaces the old detail atlas / zone overlay of terrain.js and ring.js.
//
// INVARIANT. Every layer averages to x1 and a flat normal, so the mips
// converge to the bare baked tile; until the arrays land (uMatOn = 0) the
// output IS the baked tile, bit for bit — the load fallback and the gate
// (tools/shaderprobe.mjs) both rely on it.
//
// PER FRAGMENT (terrain V_GROUND, rings L0..L1 / L0..L2 by tier):
//   1. class weights: 4 texel fetches around the fragment, bilinear weights
//      accumulated per layer -> the top two layers m1, m2 and their mix wb.
//      Source: the toroidal 3 x 3 NEAR WINDOW (uNear, 1 m: class + the bake's
//      shade tile — sun visibility and the normal it lit with) where its slot
//      holds the right tile, else the coarse 4 m class grid (uClassM).
//      Class 255 (unclassified) takes band_layers by elevation (uBands).
//   2. hex tiling (Mikkelsen 2022: 3 taps, per-cell rotation + offset,
//      height-weighted contrast-preserving blend) of A and B; textureGrad
//      only, every derivative taken outside a branch.
//   3. height blend between m1 and m2 (grass creeps between stones).
//   4. albedo: diffuse *= multiplier, faded out by distance; a macro tap of
//      the same layers at MACRO_K x the period carries texture to the far
//      field — natural ground only: a built way kind's weight (pwM) takes it
//      away (ROAD-MAT 3; check-materials row 7).
//   5. relight: E(detail normal) / E(baked normal) with the bake's own light
//      model (meta.tex.light sky / ground / sun, meta.lighting sun dir, the
//      shade tile's sun visibility; sky visibility taken as 1), cavity on the
//      sky term, clamped; a flat detail normal gives exactly 1.
//   1b. WAYS-G: the bake's ways field (waysg CONTRACT C2; uNearW0 / uNearW1,
//      the two planes, the same 3 x 3 toroidal slots, uNearWSlot): P0 = R
//      signed distance to the strip edge, G t = offset / half width, B kind |
//      hi << 4, A side-strip distance; P1 = the lane-line distance + dash, or
//      the sleeper / tread phase (cos, sin) + flight mask, B code | K / 2.
//      The way weight pw (pipeline/wayslook.json: PATH-G kinds soil / rock /
//      track their cross profile gmPathCross x the `edge` ramp; built kinds
//      asphalt / concrete / paving / sett / gravel / ballast / steps share 1 x
//      the sharper `edge_ways` ramp) hands that share of the class weights to
//      the layer of its kind (manifest path_layers), a sidewalk / apron strip
//      (hi) beyond the carriageway bound to its own; the top-two + height
//      blend draws the edge. Built kinds' NEAR features — lane lines (dashed,
//      cut at junctions), the kerb stone line, the side-strip bound, rail
//      sleepers, stair nosings / risers (+ the risers' tilted normal) — are
//      analytic, antialiased by the pixel footprint, applied as a RATIO over
//      the colour the painter baked there (meta.surface.ways.albedo: the
//      kind's mean, and the painter's own coverage model at the colour
//      texel): where the footprint reaches the texel the ratio is 1, so far
//      away the bake alone draws them. Kind none (the funicular, A4) draws
//      nothing. No ways tile resident (not landed yet) -> pw = 0, ratio 1
//      -> exactly the output without it. (uRoadM, the drawn-strip mask, is
//      gone with the strips: WAYS-G draws every way in the ground.)
//   1c. ROCK-F B, FAR WINDOW (tier 2, far_slots): beyond the near window,
//      the few 512 m tiles the focus wedge magnifies within the relight's
//      reach (attachFarWindow picks them, surface.ensureFar holds them) give
//      the same 1 m class + shade -> relight there too. Only a slot whose
//      shade tile landed reports; no far slot -> the coarse grid, exactly as
//      without it.
//   6. ROCK-F C, CLIFF (tier 2, manifest `cliff`): on the host layers (rock,
//      scree — the rock elevation band draws rock) a smooth slope ramp
//      (cliff_slope_deg) hands the face to the cliff_rock scan, hex-tiled and
//      side-projected like the wall tap (the two wall projections blended
//      across the diagonal), its detail normal in the relight, its macro
//      side-projected too.
//   7. ROCK-F C, STREAKS (tier 2, mapGLSL): the colour tile is a top-down
//      raster — on a face of slope theta one texel runs 1/cos(theta) down the
//      fall line, the vertical streaks. On steep faces the colour tap's
//      footprint is widened ALONG THE CONTOUR to the fall line's (textureGrad,
//      anisotropic: the fall line keeps its resolution, the baked light and
//      shadow stay; nothing is added). Flat ground / uMatOn 0: the same tap
//      as ever.
//   8. S4, MEASURED CONTRAST (pipeline/rough.py, rough.js groundKGrid): on
//      the scree and rock layers (GROUND_K_LAYERS; never the cliff scan) the
//      detail multiplier, cavity and normal deviate from x1 / flat k times as
//      far (floor x0.04; the mean stays x1), k from the 32 m roughness grid
//      (uRough, one bilinear tap): scree the photo's 1–5 m contrast over the
//      layer's c_mid, rock the relief. No grid (a global-tier bake, before it
//      lands), another layer, k within half a byte step of 1: untouched, bit
//      for bit.
// V_MACRO (the far rings): coarse class, one macro tap — no relight, no way
// (beyond the near window the baked colour carries the ways).
//
// WATER-G — the water surface (one source of truth: waterDecl, shared by
// the terrain and the stream ribbons, sky.js streamMaterial). The painter
// paints class 254 ONE colour up to the shore (PALETTE water deep x mod x
// baked light); over every water fragment the terrain shader adds, in
// LINEAR before the tone curve: a procedural ripple field (two octaves of
// gradient noise, each faded out once a pixel spans its wavelength, advected
// by the river flow / drifting with the wind through a two-phase flow-map
// cross-fade on waterTime), a Schlick fresnel mirroring the bake's sky
// (meta.tex.light sky), a Blinn glint of the bake's sun (x the shade tile's
// sun visibility where resident), and — V_GROUND with the materials only —
// the gravel layer seen through it (its mean-1 multiplier on the tint, hex
// tiled, offset by the ripple: refraction). The water weight: V_GROUND the
// class texels it already reads (near window 1 m, far window, else coarse
// 4 m); V_MACRO and tier 0 ONE bilinear tap of uWater (flow_res_m: water
// coverage of the coarse class grid in A, the river flow in RG — setWaterFlow
// / water.js flowField). A `sheet` lake (water.json; sky.js buildSheets draws
// its water as a sheet at level_m) is the BED here: uWater B marks it, the
// fragment keeps the painted gravel (x the gravel layer near) and draws no
// surface. Off water the output is bit for bit as before, and a terrain tile
// that meets no water (terrain.js V_WATER, waterIn below) compiles the
// program text it had before WATER-G (the builders take `water`).
import * as THREE from 'three';
import RENDER_CFG from './renderconfig.json';
import MANIFEST from '../materials/manifest.json';
import WAYSLOOK from '../../pipeline/wayslook.json';
import { MARKS, PAINT, TEETH_HB0 } from '../../game/waysdraw.js';
import { sunDirFromLighting } from './scenelight.js';
import { fetchAsset } from './assetq.js';
import { GROUND_K, GROUND_K_LAYERS, groundKGridSteps } from './rough.js';
import { classOff } from './device.js';
import { questGmLite } from './questprof.js';
import { texAniso } from './texq.js';
import { runSteps } from './decode.js';
import { slice } from './sched.js';

const CFG = Object.assign({
  macro_k: 9,                  // macro period = layer period x this
  macro_strength: 0.5,         // multiplier weight of the macro tap
  near_end_m: [0, 220, 450],   // per tier: near-scale detail fades out by here
  relight_end_m: [0, 80, 300], // per tier: relight fades out by here
  albedo_k: 1.0,               // near multiplier weight
  normal_k: 1.0,               // detail normal tilt scale
  relight_clamp: [0.55, 1.7],
  height_blend: 0.15,          // border sharpness (smaller = crisper)
  hex_contrast: 0.6,           // height bias of the hex blend
  far_slots: [0, 0, 4],        // per tier: far window slots (ROCK-F B)
  cliff_on: [0, 0, 1],         // per tier: the cliff layer on steep host faces
  cliff_slope_deg: [40, 55],   // its smooth slope ramp
  streak_on: [0, 0, 1],        // per tier: the steep-face colour footprint
  streak_slope_deg: [35, 60],  // its smooth slope ramp
  streak_k: 1.0,               // contour footprint / the fall line's
  streak_cos_min: 0.2,         // the stretch it follows is capped at 1 / this
  streak_taps: 1,              // taps across it (1: the GPU's anisotropic filter)
  rough_on: [0, 0, 1],         // per tier: S2 height self-shadow + parallax on the rough layers
  rough_layers: ['scree', 'rock', 'cliff_rock'],   // manifest keys (the cliff only where cliff_on)
  rough_end_m: [0, 0, 15],     // per tier: both fade out by here (view distance, optic-scaled)
  rough_shadow_f: [0.3, 0.75], // one height tap each: the sun ray's rise there, x the height range
  rough_shadow_max_m: 1.0,     // the march reaches at most this far toward the sun
  rough_shadow_soft: 0.15,     // full shadow once the height stands this x the range over the ray
  rough_parallax_probes: 2,    // height probes along the view ray (1: no parallax)
  rough_cliff_probes: 1,       // the same on the cliff's side projections (1: shadow only)
  rough_cliff_shadow_taps: 1,  // the cliff's shadow taps: the first (nearest) N of rough_shadow_f
  rough_parallax_k: 0.6,       // the offset's share of the true one
  rough_parallax_cos: 0.4,     // view cosine floor (offset <= k x height / this)
  rough_relight_min: [0, 0, 0.4],  // per tier: the relight clamp's floor on the rough layers (near)
  lazy_coarse_r: 2,            // LAZY: coarse-grid layers over (2r + 1)² tiles around the near tile
}, (RENDER_CFG.ground && RENDER_CFG.ground.mat) || {});
// the ground's config, read-only (tools/shaderprobe.mjs)
export const GM_CFG = CFG;

export const GM_LAYERS = MANIFEST.layers.map((l) => l.key);
export const GM_LAYER = Object.fromEntries(GM_LAYERS.map((k, i) => [k, i]));
const NL = GM_LAYERS.length;
const f1 = (x) => Number(x).toFixed(1);
const f4 = (x) => Number(x).toFixed(4);
const COSD = (d) => Math.cos(d * Math.PI / 180);
// ROCK-F C: the cliff layer and its hosts (manifest `cliff`), null = none
const CLIFF = (() => {
  const c = MANIFEST.cliff;
  if (!c || !(c.layer >= 0 && c.layer < NL) || !Array.isArray(c.host_layers)) return null;
  const hosts = c.host_layers.filter((k) => k >= 0 && k < NL && k !== c.layer);
  return hosts.length ? { layer: c.layer, hosts } : null;
})();
// tools/shaderprobe.mjs only (part of groundKey): off = every ROCK-F term
// compiled out — the program as before ROCK-F, the bit-for-bit reference;
// streak_on = a per-tier override (the tier-1 cost row). Never set in the app.
// kind_nearest: the pre-F3 look read (the nearest ways cell's byte, no 2 x 2
// weights), compiled in for shaderprobe's self-contained F3 red twin only
// rough_off: every S2 term compiled out (the program as before S2);
// rough_probes: a rough_parallax_probes override (1: the shadow-only cost row)
export const GM_PROBE = { off: false, streak_on: null, water_off: false, water_flat: false, kind_nearest: false, phase_all4: false, drop_mark: 0, teeth_line: false,
  rough_off: false, rough_probes: 0, water_point: false };
const FAR_N = (q) => (GM_PROBE.off ? 0 : Math.max(0, Math.floor(Number(CFG.far_slots[q]) || 0)));
// the cliff needs tier 2's face normal + wall derivatives
const CLIFF_ON = (q) => !GM_PROBE.off && !!CLIFF && q >= 2 && !!Number(CFG.cliff_on[q]);
const STREAK_ON = (q) => !GM_PROBE.off && q >= 1 && !!Number((GM_PROBE.streak_on || CFG.streak_on)[q]);
// S2 rough layers (rough_layers -> manifest index, only those with a height
// range: the metres A-alpha 0..1 spans, manifest stats height_range_m)
const ROUGH = (() => {
  const hr = MANIFEST.layers.map((l) => { const s = l.stats || {}; return Number((s.t2 || s.t1 || {}).height_range_m) || 0; });
  const ids = (CFG.rough_layers || []).map((k) => GM_LAYER[k]).filter((i) => i >= 0 && hr[i] > 0);
  return ids.length ? { ids, hr } : null;
})();
// S4: the k grid's layers (GROUND_K_LAYERS -> manifest index, -1 = none;
// the cliff layer never) and the byte -> k decode; KSNAP: half a byte step,
// k that close to 1 is 1 (the phase-1 look exactly)
const KL = GROUND_K_LAYERS.map((k) => (GM_LAYER[k] >= 0 && (!CLIFF || GM_LAYER[k] !== CLIFF.layer) ? GM_LAYER[k] : -1));
const K_ON = () => !GM_PROBE.off && KL.some((L) => L >= 0);
const KSNAP = 0.5 * (GROUND_K[1] - GROUND_K[0]) / 255;
// tier 2 only (the parallax needs the face normal gNg); a class that drops
// it at tier 2 (device.js CLASS_OFF: desktop-igpu) never compiles it
const ROUGH_ON = (q) => !GM_PROBE.off && !GM_PROBE.rough_off && !!ROUGH && q >= 2 && !!Number(CFG.rough_on[q]) && !classOff('rough_on');
const ROUGH_CLIFF = (q) => ROUGH_ON(q) && CLIFF_ON(q) && ROUGH.ids.includes(CLIFF.layer);
// quest-ground (2026-11-02, owner-approved subtle Quest-only pixel trades; the
// headset is GPU-bound, the ground 8.2 of its 13.6 ms): a class that drops
// gm_relight (device.js CLASS_OFF: quest) compiles kR = 0 — no detail-normal
// relight (step 5) and so no uMatB normal / cavity taps (3 a hex layer + 1 the
// border layer) within relight_end_m. The colour (the VT: baked light, the
// painter's SEALED-MAT / SEALED-WIN flats), the class blend, the near albedo
// layers, the macro, the ways field and the water stay; phones keep tier 1 whole
const RELIGHT_ON = () => !classOff('gm_relight');
// quest-steady72 (2026-11-03): the Quest picture profile's ground lite (questprof.js gm_lite: 'balanced',
// 'steady') compiles kN = kM = 0 — no near detail-layer and no macro taps (V_GROUND and V_MACRO) — and the
// hex tiling as ONE tap (gmHex → gmTap). Measured on the box (w28, APP_GROUNDMAT "Quest ground GPU
// breakdown"): detail + macro −1.3, hex −0.4 ms of the ~6.5 ms ground, all in the NEAR pixels. The
// colour (the VT: baked light, the painter's flats), the class blend, the ways field and the water stay.
// In the key (-Ql): a switch recompiles the ground programs (main.js onQuestProfile)
const GM_LITE = () => questGmLite();
const ROUGH_NP = () => Math.max(1, Math.round(GM_PROBE.rough_probes || CFG.rough_parallax_probes));
const ROUGH_NPC = () => Math.max(1, Math.round(GM_PROBE.rough_probes || CFG.rough_cliff_probes || CFG.rough_parallax_probes));
const ROUGH_NS = () => CFG.rough_shadow_f.length;
const ROUGH_NSC = () => Math.min(ROUGH_NS(), Math.max(1, Math.round(CFG.rough_cliff_shadow_taps || ROUGH_NS())));
// the cliff's rough tap: gmRoughHex itself when its probe + shadow tap counts agree
const RHC = (q) => (ROUGH_CLIFF(q) && (ROUGH_NPC() !== ROUGH_NP() || ROUGH_NSC() !== ROUGH_NS()) ? 'gmRoughHexC' : 'gmRoughHex');

// WATER-G tunables (renderconfig ground.water; APP_SURFACE.md "Water (WATER-G)")
const WCFG = Object.assign({
  flow_res_m: 8,
  ripple_m: [1.6, 0.55],
  ripple_slope: [0.1, 0.06],
  drift_mps: [0.1, 0.18],
  flow_period_s: 2.0,
  fresnel: [0.02, 0.85],
  sky_k: 1.0,
  glint: [6.0, 350],
  bed_k: 0.8,
  refract_m: 0.12,
  tint: [1, 1, 1],
  tint_srgb: [20, 62, 86],
  stream_clear: 0.45,
  sheet_ramp_m: 4.0,
  sheet_clear: 0.0,
  edge: [0.35, 0.65],
}, (RENDER_CFG.ground && RENDER_CFG.ground.water) || {});
export const WATER_CFG = WCFG;
export const WATER_VMAX = 4;            // m/s at byte 255 (= water.js FLOW_VMAX)
const WATER_ID = 254;
const GRAVEL = GM_LAYER.gravel ?? -1;
// GM_PROBE.water_off: every WATER-G term compiled out (the program text as
// before WATER-G); water_flat: the ripple amplitude 0 (an exact surface).
const W_OFF = () => !!GM_PROBE.water_off;
const W_FLAT = () => !!GM_PROBE.water_flat;
// water_point: WATER-F compiled out (V_GROUND's share point-sampled as before)
const W_POINT = () => !!GM_PROBE.water_point;
// a terrain variant's water switch (terrain.js V_WATER): false = the text
// before WATER-G, exactly
const W_ON = (water) => !!water && !W_OFF();

// WAYS-G (waysg CONTRACT C2): the ways field's decode (world/pathfield.mjs
// ENC — attachNearWindow refuses tiles of another encoding) and look. Kind id
// (B & 15) -> layer index (manifest path_layers, -1 = none); hi = B >> 4: a
// PATH-G kind's sac (track: tracktype grade), a built kind's side-strip kind.
const WAYS_ENC = { r_off: 128, r_scale: 32, g_off: 128, g_scale: 63, kind_bits: 4, a_off: 128, a_scale: 32,
  l_off: 128, l_scale: 64, d_off: 128, d_scale: 32, p_off: 128, p_scale: 127, code_bits: 3 };
export const GM_WAYS_ENC = WAYS_ENC;   // the ways tiles' encoding the GLSL decodes (gates)
const WK = WAYSLOOK.kinds;
const PL = Array.from({ length: 1 << WAYS_ENC.kind_bits }, (_, k) => {
  const L = MANIFEST.path_layers ? MANIFEST.path_layers[k] : undefined;
  return Number.isInteger(L) && L >= 0 && L < NL ? L : -1;
});
// the PATH-G kinds (cross profile, wobbly trodden edge); every other kinded
// way is BUILT (share 1, straight edge, side strip, features)
const PATH_KINDS = ['soil', 'rock', 'track'].map((n) => WK[n]);
const PSHARE = WAYSLOOK.share_by_sac.map(Number);
const PEDGE = WAYSLOOK.edge, WEDGE = WAYSLOOK.edge_ways, PCEN = WAYSLOOK.centre;
// The per-kind cross profile of the PATH-G kinds: everything kind-specific
// about a path's look lives in the one generated GLSL function
//   float gmPathCross(int kind, int hi, float g, out float nk, out float r)
// hi = B >> 4 (sac, or tracktype grade for track), g = |G - 128| / 63 (0 centre ..
// 1 strip edge). Returns the weight factor (share x profile); nk = the
// detail normal scale; r = the share of the path weight handed to the
// kind's second layer (GM_P2, -1 = none). A PATH-G kind without an entry
// here takes the default: share by sac, the trodden centre packed flatter
// (centre.normal_k at g 0), no second layer.
const PSB = 0.06;   // track band ramps, half width in g (~0.1 m on a 3 m track)
const PT = WAYSLOOK.track || null;
const PATH_CROSS = PT ? {
  // track (PATH-G AMENDMENT 1): gravel (path_layers) with share 1; the
  // grass centre strip hands the weight back to the host class by grade;
  // the wheel ruts (rut_t band) are packed soil — trail_soil as the second
  // layer, the normal flattened like a footpath's trodden centre (the darker
  // tone is the painter's, in the baked colour)
  track: {
    w: `1.0 - GM_PHOST[min(hi, ${PT.centre_host_by_grade.length - 1})]`
      + ` * (1.0 - smoothstep(${f4(PT.centre_t - PSB)}, ${f4(PT.centre_t + PSB)}, g))`,
    r: `smoothstep(${f4(PT.rut_t[0] - PSB)}, ${f4(PT.rut_t[0] + PSB)}, g)`
      + ` * (1.0 - smoothstep(${f4(PT.rut_t[1] - PSB)}, ${f4(PT.rut_t[1] + PSB)}, g))`,
    nk: `mix(1.0, ${f4(PCEN.normal_k)}, r)`,
    p2: 'trail_soil',
  },
} : {};
const PATH_CROSS_DEFAULT = {
  w: `GM_PSHARE[min(hi, ${PSHARE.length - 1})]`,
  nk: `mix(${f4(PCEN.normal_k)}, 1.0, g)`,
};
const P2 = PL.map((_, id) => {
  const name = Object.keys(WK).find((n) => WK[n] === id);
  const k = name && PATH_CROSS[name] && PATH_CROSS[name].p2;
  return k ? MANIFEST.layers.findIndex((l) => l.key === k) : -1;
});
const PATH_CROSS_GLSL = (() => {
  const branch = Object.entries(WK)
    .filter(([name]) => PATH_CROSS[name])
    .map(([name, id]) => {
      const c = PATH_CROSS[name];
      return `  if (kind == ${id}) {\n    r = ${c.r || '0.0'};\n    nk = ${c.nk};\n    return ${c.w};\n  }\n`;
    })
    .join('');
  return (PT ? `const float GM_PHOST[${PT.centre_host_by_grade.length}] = float[${PT.centre_host_by_grade.length}]`
      + `(${PT.centre_host_by_grade.map(f4).join(', ')});\n` : '')
    + `float gmPathCross(int kind, int hi, float g, out float nk, out float r) {\n${branch}`
    + `  r = 0.0;\n  nk = ${PATH_CROSS_DEFAULT.nk};\n  return ${PATH_CROSS_DEFAULT.w};\n}`;
})();
// built-kind near features (lines, kerb, sleepers, treads): the paint
// colours (wayslook, sRGB -> linear) and geometry (waysdraw MARKS / PAINT,
// wayslook ballast / steps — one source each)
const LIN = (c) => c.map((v) => { const x = v / 255; return x <= 0.04045 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4; });
const V3 = (c) => `vec3(${LIN(c).map(f4).join(', ')})`;
// the linear ratio LIN(k c) / LIN(c) per channel
const V3r = (c, k) => { const a = LIN(c), b = LIN(c.map((v) => Math.min(255, v * k))); return `vec3(${b.map((v, i) => f4(v / a[i])).join(', ')})`; };
const WB = WAYSLOOK.ballast, WS = WAYSLOOK.steps;
// (D1-MARKS) the ground marks (world/pathfield.mjs P1 codes 5 zebra / 6 xline
// / 7 grid; game/waysdraw.js MARKS.ground the look): gmMark mixes a mark into
// the colour A at footprint fw (coverage) and fk (the periodic keep) — the
// sharp read (gFW, gFW) over the painter's model (gWb, gTc), as the lane lines.
// A PATH-G kind has no painter albedo (uWayAlb 0): its ratio base is the
// look's mean tread / patch (GM_PATHBK, in WAY_BASE_GLSL). A field without
// codes 5-7 never enters gmMark.
const MG = MARKS.ground;
const MARK_GLSL = (() => {
  const Z = MG.zebra, X = MG.give_way, S = MG.stop, R = MG.grid, T = MG.give_way_tri;
  const TU1 = X.line_m / 2 + T.line_gap_m + T.len_m;   // the row's far (apex) edge upstream of the line centre
  const PZ = Z.bar_m + Z.gap_m, PX = X.dash_m + X.gap_m;
  const D = (v) => `(${v} - ${f1(WAYS_ENC.d_off)}) / ${f1(WAYS_ENC.d_scale)}`;
  return `const vec3 GM_MZEBRA = ${V3(Z.colour)};
const vec3 GM_MGIVE = ${V3(X.colour)};
const vec3 GM_MSTOP = ${V3(S.colour)};
const vec3 GM_MTRI = ${V3(T.colour)};
const vec3 GM_MSTEEL = ${V3(R.colour)};
const vec3 GM_MPIT = ${V3(R.pit)};
// a zebra / grid slot run (waysdraw MARKS.ground): phase ph in slots of P
// (K per turn), the run at slots m .. K - m - 2, a bar of width bar centred in
// each: -> the bar coverage at footprint fw (3 slot terms: exact below one
// slot; faded to the run's mean by kp), sc = the run's coverage
float gmRun(float ph, float K, float m, float P, float bar, float fw, float kp, out float sc) {
  float n = K - 2.0 * m - 1.0;
  float k = floor(ph), x = (ph - k) * P;
  sc = gmCov(ph * P, m * P, (m + n) * P, fw);
  float bc = 0.0;
  for (int j = -1; j <= 1; j++) {
    float kj = k + float(j);
    if (kj >= m && kj <= m + n - 1.0) bc += gmCov(x - float(j) * P, 0.5 * (P - bar), 0.5 * (P + bar), fw);
  }
  return kp * bc + (1.0 - kp) * sc * (bar / P);
}
// hb = P1.B >> 3: an xline's sub (1 give_way, 2 stop, TEETH_HB0 + n a
// give-way line carrying its row of n teeth: R = the along offset upstream,
// G / A the line's bounds, the row centred in them), else K / 2; an xline's
// across distances G / A at MARKS.ground.xline_o_scale per byte. The zebra /
// give-way bars exact (as the painter: never faded), the grid's sub-texel
// pitch faded to its mean (fk, gmKeep) as the painter's _octave_keep
vec3 gmMark(vec3 A, int code, int hb, vec4 w1, float fw, float fk) {
  if (code == 6) {
    float a = (w1.r - ${f1(WAYS_ENC.l_off)}) / ${f1(WAYS_ENC.l_scale)};
    float dn = (min(w1.g, w1.a) - ${f1(WAYS_ENC.d_off)}) / ${f1(MG.xline_o_scale)};
    // teeth-no-dashes (owner 2026-10-12): a line carrying its row of teeth
    // (hb > TEETH_HB0) draws the teeth only — no dashed line over the row's
    // span (the line's span is the row's); GM_TEETH_LINE (GM_PROBE.teeth_line,
    // shaderprobe's red twin) the pre-2026-10-12 read, the dashes under it too
#ifdef GM_TEETH_LINE
    if (true) {
#else
    if (hb <= ${TEETH_HB0}) {
#endif
      float lw = hb == 2 ? ${f4(S.line_m)} : ${f4(X.line_m)};
      float c = gmCov(a, -0.5 * lw, 0.5 * lw, fw) * clamp(0.5 + dn / fw, 0.0, 1.0);
      if (hb != 2) {
        float x = mod(max(dn, 0.0), ${f4(PX)});
        c *= gmCov(x, 0.0, ${f4(X.dash_m)}, fw) + gmCov(x - ${f4(PX)}, 0.0, ${f4(X.dash_m)}, fw);
      }
      A = mix(A, hb == 2 ? GM_MSTOP : GM_MGIVE, c);
    }
    if (hb >= ${TEETH_HB0 + 1}) {
      // the row of n teeth (waysdraw teethAt): a = u upstream of the line,
      // x from the row's left edge (centred in the line's span), the nearest
      // tooth's axis distance against its half base at u
      float n = float(hb - ${TEETH_HB0});
      float gq = (w1.g - ${f1(WAYS_ENC.d_off)}) / ${f1(MG.xline_o_scale)}, aq = (w1.a - ${f1(WAYS_ENC.d_off)}) / ${f1(MG.xline_o_scale)};
      float xr = 0.5 * (gq - aq + n * ${f4(T.base_m + T.gap_m)} - ${f4(T.gap_m)});
      float k = clamp(floor((xr + ${f4(T.gap_m / 2)}) / ${f4(T.base_m + T.gap_m)}), 0.0, n - 1.0);
      float cx = abs(xr - k * ${f4(T.base_m + T.gap_m)} - ${f4(T.base_m / 2)});
      float hw = ${f4(T.base_m / 2)} * clamp((${f4(TU1)} - a) / ${f4(T.len_m)}, 0.0, 1.0);
      float tc = gmCov(a, ${f4(TU1 - T.len_m)}, ${f4(TU1)}, fw) * clamp(0.5 + (hw - cx) / fw, 0.0, 1.0);
      A = mix(A, GM_MTRI, tc);
    }
    return A;
  }
  float r = clamp(0.5 + ${D('w1.r')} / fw, 0.0, 1.0);
  float K = 2.0 * float(hb);
  float ph = gmPhase(w1, K), sc;
  if (code == 5) {
    float bc = gmRun(ph, K, ${f1(Z.margin_slots)}, ${f4(PZ)}, ${f4(Z.bar_m)}, fw, 1.0, sc);
    return mix(A, GM_MZEBRA, r * bc);
  }
  if (code == 7) {
    float bc = gmRun(ph, K, ${f1(R.margin_slots)}, ${f4(R.pitch_m)}, ${f4(R.bar_m)}, fw, gmKeep(${f4(R.pitch_m)}, fk), sc);
    return A * (1.0 - r * sc) + r * (GM_MPIT * (sc - bc) + GM_MSTEEL * bc);
  }
  return A;
}`;
})();
// the look's mean tread / patch per PATH-G kind (vec3(0.0) elsewhere): the
// albedo base of a kind without painter albedo, V_GROUND's and the deck's
const PATHBK = Array.from({ length: 16 }, (_, k) => {
  const name = Object.keys(WK).find((n) => WK[n] === k);
  const c = name && PATH_KINDS.includes(k) ? WAYSLOOK.colour[name] : null;
  return c && c.tread && c.patch ? V3(c.tread.map((v, i) => (v + c.patch[i]) / 2)) : 'vec3(0.0)';
});
// the way look's colours + coverage helpers (deckwaymat takes only these);
// WAY_FEAT_GLSL adds the P1 phase + the painted marks (V_GROUND)
const WAY_BASE_GLSL = `const vec3 GM_WLINE = ${V3(WAYSLOOK.lines.colour)};
const vec3 GM_PATHBK[16] = vec3[16](${PATHBK.join(', ')});
const vec3 GM_WKERB = ${V3(WAYSLOOK.kerb.colour)};
const vec3 GM_WSLEEPER = ${V3(WB.colour_sleeper)};
// treads (WAYSG-L4b, wayslook steps.luma): the nosing / riser as the tread's
// own colour x nosing_k / riser_k (sRGB), linear here as a per-channel ratio
// over the way's albedo Bk — the painter sets the tread's luminance from the
// ground ring and scales all three, the ratio Ash / Abk stays the painter's.
// Without steps.luma the look's fixed colours (the pre-L4b read).
${WS.luma ? `const vec3 GM_WNOSING_K = ${V3r(WS.colour.tread, WS.luma.nosing_k)};
const vec3 GM_WRISER_K = ${V3r(WS.colour.tread, WS.luma.riser_k)};
#define GM_WNOSING (Bk * GM_WNOSING_K)
#define GM_WRISER (Bk * GM_WRISER_K)` : `const vec3 GM_WNOSING = ${V3(WS.colour.nosing)};
const vec3 GM_WRISER = ${V3(WS.colour.riser)};`}
const float GM_WLINE_M = ${f4(MARKS.line_m)};
const float GM_WKERB_M = ${f4(PAINT.kerb_line_m)};
// coverage 0..1 of [a, b] by a box footprint w centred at x (surfacegen _box_cov)
float gmCov(float x, float a, float b, float w) { return clamp((min(x + 0.5 * w, b) - max(x - 0.5 * w, a)) / w, 0.0, 1.0); }
// how much of a period wl survives a footprint w (surfacegen _octave_keep)
float gmKeep(float wl, float w) { float t = clamp((wl / w - 2.0) / 2.0, 0.0, 1.0); return t * t * (3.0 - 2.0 * t); }`;
const WAY_FEAT_GLSL = `${WAY_BASE_GLSL}
// the phase (in periods, 0..K) of a P1 (cos, sin) pair
float gmPhase(vec4 w1, float K) {
  return mod(atan(w1.a - ${f1(WAYS_ENC.p_off)}, w1.g - ${f1(WAYS_ENC.p_off)}) / 6.2831853 * K, max(K, 1.0));
}
${MARK_GLSL}`;
// a cheap stable digest of everything the GLSL takes from wayslook.json +
// path_layers (the program cache key; the bundle has no file bytes to sha)
const PATH_KEY = (() => {
  const t = JSON.stringify([PL, P2, PSHARE, PEDGE, WEDGE, PATH_KINDS, PATH_CROSS_GLSL, WAY_FEAT_GLSL, WB, WS]);
  let h = 2166136261;
  for (let i = 0; i < t.length; i++) h = Math.imul(h ^ t.charCodeAt(i), 16777619);
  return (h >>> 0).toString(16);
})();

// ------------------------------------------------------------- uniforms
// Shared OBJECTS: every material references them, the renderer reads .value
// on each draw — loads swap values, never recompile.
function neutralArray() {
  const t = new THREE.DataArrayTexture(new Uint8Array(4 * NL).fill(128), 1, 1, NL);
  t.needsUpdate = true;
  return t;
}
// R8d6a THE TILE COPY WITHOUT READS: three's copyTextureToTexture for a
// DataTexture source into an uploaded 2D RGBA u8 texture at level 0, call for
// call — the bind of setTexture2D's up-to-date branch, the unpack state it
// sets, texSubImage2D, generateMipmap when the target asks, the unbind —
// except its five gl.getParameter(UNPACK_*) reads: each is a SYNC round trip
// that waits behind the GPU's queued frame (~50 ms a copy on the desktop iGPU,
// 26 long tasks in quiet window 3). Its restore writes the values three reads
// there: 0, the GL default — no client code leaves them otherwise (canopygen /
// groundgen reset them to 0; check-mapfirst j pins the stream and that).
export function subUpload(renderer, src, dst, pos) {
  const gl = renderer.getContext(), p = renderer.properties.get(dst);
  if (!p.__webglTexture || p.__version !== dst.version) throw new Error('subUpload: the target texture is not uploaded (initTexture first)');
  if (dst.format !== THREE.RGBAFormat || dst.type !== THREE.UnsignedByteType || dst.isData3DTexture || dst.isDataArrayTexture) {
    throw new Error('subUpload: a 2D RGBA u8 target only');
  }
  const img = src.image;
  renderer.state.bindTexture(gl.TEXTURE_2D, p.__webglTexture, gl.TEXTURE0);
  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, dst.flipY);
  gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, dst.premultiplyAlpha);
  gl.pixelStorei(gl.UNPACK_ALIGNMENT, dst.unpackAlignment);
  gl.pixelStorei(gl.UNPACK_ROW_LENGTH, img.width);
  gl.pixelStorei(gl.UNPACK_IMAGE_HEIGHT, img.height);
  gl.pixelStorei(gl.UNPACK_SKIP_PIXELS, 0);
  gl.pixelStorei(gl.UNPACK_SKIP_ROWS, 0);
  gl.pixelStorei(gl.UNPACK_SKIP_IMAGES, 0);
  gl.texSubImage2D(gl.TEXTURE_2D, 0, pos.x, pos.y, img.width, img.height, gl.RGBA, gl.UNSIGNED_BYTE, img.data);
  gl.pixelStorei(gl.UNPACK_ROW_LENGTH, 0);
  gl.pixelStorei(gl.UNPACK_IMAGE_HEIGHT, 0);
  gl.pixelStorei(gl.UNPACK_SKIP_PIXELS, 0);
  gl.pixelStorei(gl.UNPACK_SKIP_ROWS, 0);
  gl.pixelStorei(gl.UNPACK_SKIP_IMAGES, 0);
  if (dst.generateMipmaps) gl.generateMipmap(gl.TEXTURE_2D);
  renderer.state.unbindTexture();
}
// R8d6b ONE TILE WRITE IN ROW BANDS: a w x h RGBA block (data, row 0 first)
// into dst at (x, y) as BAND_ROWS-row subUploads, each its own upload piece
// of the compile queue (compileq q.steps: a write that stalls behind the
// queued frame ends its task, the next band goes in the next; quiet window 4:
// one 512² write was a 45-56 ms task). The union of the bands is the one-shot
// write, byte for byte (check-mapfirst k). live() false = a newer write of the
// same slot took over: the rest of these bands are skipped; done() runs after
// the last band (the slot record: the shader reads the slot only then).
export const BAND_ROWS = 64;
export function bandPieces(renderer, data, w, h, dst, x, y, live, done) {
  const out = [];
  for (let r = 0; r < h; r += BAND_ROWS) {
    const n = Math.min(BAND_ROWS, h - r), last = r + n >= h;
    out.push({ bytes: w * n * 4, run: () => {
      if (!live()) return;
      subUpload(renderer, { image: { data: data.subarray(r * w * 4, (r + n) * w * 4), width: w, height: n } }, dst, { x, y: y + r });
      if (last && done) done();
    } });
  }
  return out;
}

// WATER-F THE NEAR WATER MASK (uNearWM). A slot's levels: level 0 = 255 where
// the tile's class byte is 254, else 0; level k = round(255 x the share of
// class 254 among its 2^k x 2^k texels) — counted, never re-rounded level on
// level. Levels while T / 2^k is whole (512: 10, the last one texel a slot),
// so no level mixes two slots. Every level from the CPU (no generateMipmap,
// never framebuffer-attached: the Quest GL rules, APP_SURFACE_PERF).
export function waterMaskLevels(T) {
  let n = 1;
  while (T % (1 << n) === 0 && (T >> n) >= 1) n++;
  return n;
}
export function waterMaskMips(cls, T) {
  const nL = waterMaskLevels(T), out = [];
  let cnt = new Uint32Array(T * T), w = T;
  const l0 = new Uint8Array(T * T);
  for (let i = 0; i < T * T; i++) if (cls[i] === WATER_ID) { cnt[i] = 1; l0[i] = 255; }
  out.push(l0);
  for (let k = 1; k < nL; k++) {
    const h = w >> 1, nc = new Uint32Array(h * h), v = new Uint8Array(h * h), area = 4 ** k;
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < h; x++) {
        const i = 2 * y * w + 2 * x, c = cnt[i] + cnt[i + 1] + cnt[i + w] + cnt[i + w + 1];
        nc[y * h + x] = c;
        v[y * h + x] = Math.round(255 * c / area);
      }
    }
    out.push(v);
    cnt = nc; w = h;
  }
  return out;
}
// the mask texture of a 3T window (all levels 0: no water), uploaded once
function waterMaskTex(renderer, T) {
  const NP = 3 * T, mips = [];
  for (let k = 0, s = NP; k < waterMaskLevels(T); k++, s >>= 1) mips.push({ data: new Uint8Array(s * s), width: s, height: s });
  const t = new THREE.DataTexture(mips[0].data, NP, NP, THREE.RedFormat, THREE.UnsignedByteType);
  t.mipmaps = mips;
  t.minFilter = THREE.LinearMipmapLinearFilter;
  t.magFilter = THREE.LinearFilter;
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.anisotropy = 4;
  t.generateMipmaps = false;
  t.unpackAlignment = 1;
  t.needsUpdate = true;
  renderer.initTexture(t);
  return t;
}
// one R8 block into level `level` of dst (subUpload's call stream, RED)
function subUploadR8(renderer, data, w, h, dst, x, y, level) {
  const gl = renderer.getContext(), p = renderer.properties.get(dst);
  if (!p.__webglTexture || p.__version !== dst.version) throw new Error('subUploadR8: the target texture is not uploaded (initTexture first)');
  renderer.state.bindTexture(gl.TEXTURE_2D, p.__webglTexture, gl.TEXTURE0);
  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
  gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
  gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
  gl.texSubImage2D(gl.TEXTURE_2D, level, x, y, w, h, gl.RED, gl.UNSIGNED_BYTE, data);
  renderer.state.unbindTexture();
}
// a slot's mask levels as upload pieces (level 0 in bands of the window's
// band bytes, the rest one piece); live() as bandPieces
function waterMaskPieces(renderer, mips, T, dst, s, live) {
  const out = [], rows = BAND_ROWS * 4;
  for (let r = 0; r < T; r += rows) {
    const n = Math.min(rows, T - r);
    out.push({ bytes: T * n, run: () => { if (live()) subUploadR8(renderer, mips[0].subarray(r * T, (r + n) * T), T, n, dst, (s % 3) * T, Math.floor(s / 3) * T + r, 0); } });
  }
  let b = 0;
  for (let k = 1; k < mips.length; k++) b += mips[k].length;
  if (mips.length > 1) {
    out.push({ bytes: b, run: () => {
      if (!live()) return;
      for (let k = 1; k < mips.length; k++) {
        const t = T >> k;
        subUploadR8(renderer, mips[k], t, t, dst, (s % 3) * t, Math.floor(s / 3) * t, k);
      }
    } });
  }
  return out;
}

function dataTex(bytes, w, h, format) {
  const t = new THREE.DataTexture(bytes, w, h, format, THREE.UnsignedByteType);
  t.minFilter = t.magFilter = THREE.NearestFilter;
  t.generateMipmaps = false;
  t.unpackAlignment = 1;
  t.needsUpdate = true;
  return t;
}
export const GU = {
  uMatA: { value: neutralArray() },
  uMatB: { value: neutralArray() },
  uMatOn: { value: 0 },
  // LAZY: bit L = layer L's slices are real; all set until loadGroundMaterials
  // binds its arrays (then: the layers landed so far)
  uMatMask: { value: NL >= 32 ? 0xffffffff : 2 ** NL - 1 },
  // near window: R class id, G sun visibility, B/A 0.5 + 0.5 n.x / n.z
  uNear: { value: dataTex(new Uint8Array([255, 255, 128, 128]), 1, 1, THREE.RGBAFormat) },
  // per slot (tx, ty) of the tile it holds, z = class ok, w = shade ok
  uNearSlot: { value: Array.from({ length: 9 }, () => new THREE.Vector4(-1e5, -1e5, 0, 0)) },
  // WAYS-G ways window (same slots), the field's two planes (WAYS_ENC; the
  // empty cell = R 255, B 0: no way). uNearWSlot: (tx, ty) of the ways tile a
  // slot holds, z = ways ok (its own record: a ways tile can land first)
  uNearW0: { value: dataTex(new Uint8Array([255, 255, 0, 0]), 1, 1, THREE.RGBAFormat) },
  uNearW1: { value: dataTex(new Uint8Array([255, 128, 0, 128]), 1, 1, THREE.RGBAFormat) },
  uNearWSlot: { value: Array.from({ length: 9 }, () => new THREE.Vector4(-1e5, -1e5, 0, 0)) },
  // WATER-F: the near window's class-254 mask (same slots, R8, the slot's
  // box-filtered mips from its class bytes; made at the first wet tile:
  // uNearWMOn 1) — V_GROUND's water share past one texel of footprint
  uNearWM: { value: dataTex(new Uint8Array([0]), 1, 1, THREE.RedFormat) },
  uNearWMOn: { value: 0 },
  // per kind id the painter's mean albedo (meta.surface.ways.albedo, linear;
  // 0 = unknown: no feature ratio for that kind)
  uWayAlb: { value: Array.from({ length: 16 }, () => new THREE.Vector3(0, 0, 0)) },
  uClassM: { value: dataTex(new Uint8Array([255]), 1, 1, THREE.RedFormat) },
  // x coarse cell m, y/z coarse w/h, w classmap cell m
  uClassInfo: { value: new THREE.Vector4(4, 1, 1, 1) },
  // treeline, rock, snow (m a.s.l.)
  uBands: { value: new THREE.Vector4(2250, 2650, 1e9, 0) },
  uSunL: { value: new THREE.Vector3(0, 1, 0) },   // the world's sun: configureGround
  uSky: { value: new THREE.Vector3(1, 1, 1) },
  uGnd: { value: new THREE.Vector3(0.5, 0.5, 0.5) },
  uSunC: { value: new THREE.Vector3(2, 2, 2) },
  // ROCK-F B far window: slot i = rows i * tpx.. of one RGBA8 texture (the
  // near window's texel layout); uFarIdx: one texel per 512 m tile of the
  // world, R = 1 + the slot holding it with its shade, 0 = none
  uFar: { value: dataTex(new Uint8Array([255, 255, 128, 128]), 1, 1, THREE.RGBAFormat) },
  uFarIdx: { value: dataTex(new Uint8Array([0]), 1, 1, THREE.RedFormat) },
  // WATER-G: the animation clock (sky.js waterTime, main.js ticks it); the
  // water texture (RG flow 128 + 127 v / WATER_VMAX per axis, B sheet bed,
  // A coverage of class 254; LINEAR, clamped) and its xy = 1 / extent m,
  // z = cell m. Until the class grid lands: no water anywhere.
  uWT: { value: 0 },
  uWater: { value: dataTex(new Uint8Array([128, 128, 0, 0]), 1, 1, THREE.RGBAFormat) },
  uWaterInfo: { value: new THREE.Vector4(1e-9, 1e-9, 8, 0) },
  // S4: the k grid (rough.js groundKGrid: RG = k of GROUND_K_LAYERS, LINEAR,
  // clamped) and its xy = 1 / extent m, z = 1 once a grid landed (0: k = 1)
  uRough: { value: dataTex(new Uint8Array([0, 0]), 1, 1, THREE.RGFormat) },
  uRoughInfo: { value: new THREE.Vector4(0, 0, 0, 0) },
  // WATER-G: 1 once terrain.applyWater ran (the water variants are warm):
  // before, every water term reads ww = 0 — the pre-grid frames as ever
  uWaterOn: { value: 0 },
  // STABLE TEXT: every renderconfig / manifest / bake NUMBER the ground GLSL
  // reads (fades, weights, clamps, slopes, per-layer metres, the water look,
  // the classmap tile px) is one of these — a changed value never changes the
  // program text (the browser's program cache keys on the text). Switches,
  // loop counts and the layer map (indices) stay compile constants. Filled
  // by gmFill with the very numbers the text carried (f1 / f4 rounding).
  // uGmFade[tier]: near fade start / end, relight fade start / end (m);
  // uGmRoughF[tier]: the S2 fade start / end, its relight floor (0: none)
  uGmFade: { value: [0, 1, 2].map(() => new THREE.Vector4()) },
  uGmRoughF: { value: [0, 1, 2].map(() => new THREE.Vector4()) },
  // macro_strength, 1 / macro_k, albedo_k, normal_k
  uGmMat: { value: new THREE.Vector4() },
  // relight_clamp lo / hi, height_blend, hex_contrast
  uGmMat2: { value: new THREE.Vector4() },
  // cos of cliff_slope_deg hi / lo, of streak_slope_deg hi / lo
  uGmSlope: { value: new THREE.Vector4() },
  // the cliff layer's 1 / m_per_tile, its macro (/ macro_k), streak_cos_min, streak_k
  uGmCliff: { value: new THREE.Vector4() },
  // S2: rough_shadow_max_m, rough_shadow_soft, rough_parallax_k, rough_parallax_cos
  uGmRough: { value: new THREE.Vector4() },
  // per layer: 1 / m_per_tile, the S2 height range (m); per shadow tap: rough_shadow_f
  uGmInvM: { value: new Array(NL).fill(0) },
  uGmHR: { value: new Array(NL).fill(0) },
  uGmRF: { value: (CFG.rough_shadow_f || []).map(() => 0) },
  // classmap tile px (configureGround)
  uGmTpx: { value: 512 },
  // WATER-G look: (ripple_slope 1 / 2, 1 / ripple_m 1 / 2), the octaves' fade
  // footprints (0.15 / 0.4 x ripple_m 1, 2), their drift (xy 1, zw 2),
  // (1 / flow_period_s, flow_period_s, sky_k, refract_m), (fresnel F0, 1 - F0,
  // max, glint k), (glint n, edge lo / hi, bed_k), (tint rgb, 1 / the gravel's m_per_tile)
  uWtrA: { value: new THREE.Vector4() },
  uWtrB: { value: new THREE.Vector4() },
  uWtrD: { value: new THREE.Vector4() },
  uWtrT: { value: new THREE.Vector4() },
  uWtrF: { value: new THREE.Vector4() },
  uWtrG: { value: new THREE.Vector4() },
  uWtrH: { value: new THREE.Vector4() },
};
const _near = { tpx: 512 };   // classmap tile px (uniform uGmTpx, set by configureGround)
// the uniforms of STABLE TEXT, each the number the GLSL text used to carry
const n1 = (x) => Number(f1(x)), n4 = (x) => Number(f4(x));
function gmFill() {
  for (let q = 0; q < 3; q++) {
    const NE = Math.max(1, CFG.near_end_m[q] || 0), RE = Math.max(1, CFG.relight_end_m[q] || 0);
    const PE = Math.max(1, (CFG.rough_end_m || [])[q] || 0);
    GU.uGmFade.value[q].set(n1(NE * 0.5), n1(NE), n1(RE * 0.6), n1(RE));
    GU.uGmRoughF.value[q].set(n1(PE * 0.6), n1(PE), n4(Number((CFG.rough_relight_min || [])[q]) || 0), 0);
  }
  GU.uGmMat.value.set(n4(CFG.macro_strength), n4(1 / CFG.macro_k), n4(CFG.albedo_k), n4(CFG.normal_k));
  GU.uGmMat2.value.set(n4(CFG.relight_clamp[0]), n4(CFG.relight_clamp[1]), n4(CFG.height_blend), n4(CFG.hex_contrast));
  GU.uGmSlope.value.set(n4(COSD(CFG.cliff_slope_deg[1])), n4(COSD(CFG.cliff_slope_deg[0])),
    n4(COSD(CFG.streak_slope_deg[1])), n4(COSD(CFG.streak_slope_deg[0])));
  const iC = CLIFF ? 1 / MANIFEST.layers[CLIFF.layer].m_per_tile : 0;
  GU.uGmCliff.value.set(n4(iC), n4(iC / CFG.macro_k), n4(CFG.streak_cos_min), n4(CFG.streak_k));
  GU.uGmRough.value.set(n4(CFG.rough_shadow_max_m), n4(CFG.rough_shadow_soft), n4(CFG.rough_parallax_k), n4(CFG.rough_parallax_cos));
  MANIFEST.layers.forEach((l, i) => { GU.uGmInvM.value[i] = n4(1 / l.m_per_tile); GU.uGmHR.value[i] = ROUGH ? n4(ROUGH.hr[i]) : 0; });
  (CFG.rough_shadow_f || []).forEach((f, j) => { GU.uGmRF.value[j] = n4(f); });
  GU.uGmTpx.value = _near.tpx;
  const w = WCFG;
  const [L1, L2] = w.ripple_m.map(Number), [S1, S2] = w.ripple_slope.map(Number), [D1, D2] = w.drift_mps.map(Number);
  const T = Math.max(0.1, Number(w.flow_period_s)), [F0, FM] = w.fresnel.map(Number), [GK, GN] = w.glint.map(Number);
  const [E0, E1] = w.edge.map(Number), tint = w.tint.map(Number);
  GU.uWtrA.value.set(n4(S1), n4(S2), n4(1 / L1), n4(1 / L2));
  GU.uWtrB.value.set(n4(0.15 * L1), n4(0.4 * L1), n4(0.15 * L2), n4(0.4 * L2));
  // the octaves' own drift directions (lake wind), rotated off each other
  GU.uWtrD.value.set(n4(0.94 * D1), n4(0.34 * D1), n4(-0.77 * D2), n4(0.64 * D2));
  GU.uWtrT.value.set(n4(1 / T), n4(T), n4(Number(w.sky_k)), n4(w.refract_m));
  GU.uWtrF.value.set(n4(F0), n4(1 - F0), n4(FM), n4(GK));
  GU.uWtrG.value.set(n1(GN), n4(E0), n4(E1), n4(w.bed_k));
  GU.uWtrH.value.set(n4(tint[0]), n4(tint[1]), n4(tint[2]), GRAVEL >= 0 ? n4(1 / MANIFEST.layers[GRAVEL].m_per_tile) : 0);
}
gmFill();

export function bindGround(uniforms) { Object.assign(uniforms, GU); }

// WATER-G's cache-key part (every terrain variant + the stream ribbons):
// what the text carries — switches and layer indices only, the look's
// numbers are uniforms (STABLE TEXT)
export function waterKey() {
  return `w${GRAVEL},${Number(WCFG.bed_k) > 0 ? 1 : 0}`
    + (W_OFF() || W_FLAT() ? `-W${W_OFF() ? 1 : 0}${W_FLAT() ? 1 : 0}` : '') + (W_POINT() ? '-Wp' : '');
}

// The cache-key part: everything string-interpolated into the GLSL — the
// layer map, switches and counts (STABLE TEXT: every NUMBER of renderconfig
// ground.mat, the manifest and the bake is a uniform, never in the text or
// here; a changed value reuses the browser's compiled programs)
export function groundKey() {
  return `gm${NL},${NA}-${CL}-${MANIFEST.band_layers}-n${SNOW}-p${PATH_KEY}`
    + `-f${CFG.far_slots}-c${CLIFF ? `${CLIFF.layer}:${CLIFF.hosts}` : 'x'},${CFG.cliff_on}`
    + `-s${CFG.streak_on},${Math.max(1, Math.round(CFG.streak_taps))}`
    + `-r${CFG.rough_on},${ROUGH ? ROUGH.ids : 'x'},${ROUGH_NP()},${ROUGH_NPC()},${ROUGH_NS()},${ROUGH_NSC()},`
    + `${CFG.rough_relight_min.map((v) => (Number(v) ? 1 : 0))}`
    + (GM_PROBE.rough_off ? '-R0' : '') + (classOff('rough_on') ? '-Rc' : '') + (RELIGHT_ON() ? '' : '-Lc') + (GM_LITE() ? '-Ql' : '')
    + `-k${K_ON() ? KL : 'x'},${GROUND_K}`
    + (GM_PROBE.off || GM_PROBE.streak_on ? `-P${GM_PROBE.off ? 1 : 0}${GM_PROBE.streak_on || ''}` : '')
    + (GM_PROBE.kind_nearest ? '-Kn' : '')
    + (GM_PROBE.phase_all4 ? '-A4' : '')
    + (GM_PROBE.drop_mark ? `-D${GM_PROBE.drop_mark}` : '')
    + (GM_PROBE.teeth_line ? '-Tl' : '');
}

// ------------------------------------------------------------- world facts
// Once per world (the tile size: uniform uGmTpx): the bake's light model, sun, elevation bands.
export function configureGround(meta) {
  const L = meta && meta.tex && meta.tex.light;
  if (L && Array.isArray(L.sky) && Array.isArray(L.ground) && Array.isArray(L.sun)) {
    GU.uSky.value.fromArray(L.sky);
    GU.uGnd.value.fromArray(L.ground);
    GU.uSunC.value.fromArray(L.sun);
  }
  const d = sunDirFromLighting(meta && meta.lighting);
  if (!d) throw new Error('world meta.lighting missing (sun_az_deg / sun_alt_deg): rebuild this world (Build)');
  GU.uSunL.value.fromArray(d);
  setGroundBands(meta && meta.bands);
  const s = meta && meta.surface;
  const alb = s && s.ways && s.ways.albedo;
  for (let k = 0; k < 16; k++) {
    const c = alb && Array.isArray(alb[String(k)]) && alb[String(k)].length === 3 ? alb[String(k)] : null;
    GU.uWayAlb.value[k].fromArray(c ? LIN(c) : [0, 0, 0]);
  }
  if (s && s.tiles && s.cell_m > 0) {
    _near.tpx = Math.round(s.tiles.tile_m / s.cell_m);
    GU.uGmTpx.value = _near.tpx;
    GU.uClassInfo.value.w = s.cell_m;
  }
}
export function setGroundBands(bands) {
  if (!bands) return;
  const v = GU.uBands.value;
  if (Number.isFinite(Number(bands.treeline_m))) v.x = Number(bands.treeline_m);
  if (Array.isArray(bands.rock_m) && bands.rock_m.length === 2) v.y = bands.rock_m[0];
  if (Array.isArray(bands.snow_m) && bands.snow_m.length === 2) v.z = bands.snow_m[0];
}

// the coarse class grid (raw class ids, the bake's surface/coarse.png)
export function setCoarseClasses(f) {
  const tex = dataTex(f.data, f.w, f.h, THREE.RedFormat);
  const old = GU.uClassM.value;
  GU.uClassM.value = tex;
  if (old) old.dispose();
  GU.uClassInfo.value.set(f.cell, f.w, f.h, GU.uClassInfo.value.w);
  _lz.coarse = f;   // LAZY: the margin's classes (needAround)
  _lz.cbits.clear();
  _lz.center = '';
  if (_lz.at) needAround(_lz.at[0], _lz.at[1]);
  _wat.cls = f;
  buildWaterTexture();
  buildWaterBoxes(f);
}

// S4: the measured roughness (rough.js loadRough; null = none) -> the k grid
// of the scree / rock layers (their manifest c_mid: the contrast the drawn
// multiplier carries at k 1). setGroundRoughSteps = THE set: the grid in
// steps (rough.js groundKGridSteps), the uniforms swapped in its last step
// (main.js runs it, 'gm.rough'); setGroundRough = its drain
export function setGroundRough(R) {
  const it = setGroundRoughSteps(R);
  for (;;) { const r = it.next(); if (r.done) return r.value; }
}
export function* setGroundRoughSteps(R) {
  const cMid = {};
  for (const key of GROUND_K_LAYERS) {
    const l = MANIFEST.layers[GM_LAYER[key]], st = l && l.stats;
    cMid[key] = Number(st && (st.t2 || st.t1 || {}).c_mid) || 0;
  }
  const g = yield* groundKGridSteps(R, cMid), old = GU.uRough.value;
  if (!g) {
    GU.uRoughInfo.value.set(0, 0, 0, 0);
    return;
  }
  const tex = dataTex(g.data, g.w, g.h, THREE.RGFormat);
  tex.minFilter = tex.magFilter = THREE.LinearFilter;
  GU.uRough.value = tex;
  if (old) old.dispose();
  GU.uRoughInfo.value.set(1 / (g.cell * g.w), 1 / (g.cell * g.h), 1, 0);
}

// ------------------------------------------------------------- water texture
// WATER-G. One RGBA8 texture at flow_res_m (a whole multiple of the coarse
// cell): A = the share of class 254 among the coarse cells it covers, RG =
// the river flow there (water.js flowField: river areas only; lakes, ponds
// and any other water (128, 128) = still, the shader's drift alone), B = 255
// on a sheet lake's water (sheet(x, z) at a coarse cell centre) and one
// pixel around it — so the bilinear B is 1 over the whole bed, shore band
// included (no surface under the sheet's transparent rim). Built once the
// class grid lands, again when the flow / the sheets do (setWaterFlow, sky.js
// buildRivers; setWaterSheets, sky.js buildSheets). LINEAR: the far rings /
// tier 0 read the shore as the coverage's smooth 0.5 contour, the flow
// blends to still at the banks.
// R8d: the build is a STEP GENERATOR (waterTexSteps: a step per texture row
// of each pass) run by ONE coalesced async build (buildWaterTexture): every
// setter bumps `gen` and returns at once; the running build stops at its
// next step once a newer request came, and the build loop restarts on the
// LATEST inputs. The texture SWAP is deferred to the end of the build that
// saw no newer request (it used to happen inside the setter): the same
// bytes for the same inputs, only later; one swap per settled burst.
// waterTexIdle() = the running build (the gate awaits it).
const _wat = { cls: null, flow: null, sheet: null, gen: 0, run: null };
// the program text as compiled: comments, indentation and blank lines out
// (smaller text for every compile; the GPU program cache keys on it). Inner
// spaces stay — the gates read the statements as written. No block comments.
export function glslMin(s) {
  return s.replace(/\/\/[^\n]*/g, '').replace(/^[ \t]+/gm, '').replace(/[ \t]+$/gm, '').replace(/\n{2,}/g, '\n');
}
// WATER-G: V_GROUND's water term on / off (terrain.js applyWater; a new
// world starts off) — a uniform, every ground program carries the term
export function setWaterOn(on) { GU.uWaterOn.value = on ? 1 : 0; }
export function setWaterFlow(fn) {
  _wat.flow = typeof fn === 'function' ? fn : null;
  buildWaterTexture();
}
export function setWaterSheets(fn) {
  _wat.sheet = typeof fn === 'function' ? fn : null;
  buildWaterTexture();
}
function buildWaterTexture() {
  _wat.gen++;
  if (!_wat.run) _wat.run = _waterBuild();
}
export function waterTexIdle() { return _wat.run || Promise.resolve(); }
// the build loop: the inputs at its start, steps until done or superseded
// (-> null), then again while a newer request came; swaps only a build whose
// gen is still the latest. Starts at `await slice()` (setters in one task:
// one build): a microtask late while the caller's slice has budget left,
// else after a yield (R8d2) — the first step never lands in a setter's long
// task (the live probe's 530 ms 'water.texture' task was the class grid's
// caller, 2 ms of it the texture). `run` clears in the same task as the
// loop's last check.
async function _waterBuild() {
  try {
    await slice();
    for (let g = -1; g !== _wat.gen;) {
      g = _wat.gen;
      const f = _wat.cls;
      if (!f || !f.data || !(f.w > 0) || !(f.h > 0)) continue;
      const live = () => g === _wat.gen;
      const r = await runSteps(whileLive(waterTexSteps(f, _wat.flow, _wat.sheet), live), 'water.texture');
      if (r && live()) _waterSwap(r);
    }
  } finally { _wat.run = null; }
}
// a step generator that stops (-> null) at its next step once live() fails
function* whileLive(it, live) {
  for (;;) {
    if (!live()) return null;
    const s = it.next();
    if (s.done) return s.value;
    yield;
  }
}
function _waterSwap({ d, nx, ny, cell }) {
  const tex = dataTex(d, nx, ny, THREE.RGBAFormat);
  tex.minFilter = tex.magFilter = THREE.LinearFilter;
  const old = GU.uWater.value;
  GU.uWater.value = tex;
  if (old) old.dispose();
  GU.uWaterInfo.value.set(1 / (nx * cell), 1 / (ny * cell), cell, 0);
}
// the texture bytes of class grid f, flow fn, sheet fn (null = none): a
// step per row of the coverage / flow pass and of the sheet-dilate pass
// -> {d, nx, ny, cell}. Pure (the gate drains it against the pre-R8d bytes).
export function* waterTexSteps(f, flow, sheet) {
  const r = Math.max(1, Math.round(WCFG.flow_res_m / f.cell));
  const cell = r * f.cell, nx = Math.ceil(f.w / r), ny = Math.ceil(f.h / r);
  const d = new Uint8Array(nx * ny * 4);
  const sh = sheet ? new Uint8Array(nx * ny) : null;
  const enc = (v) => Math.max(1, Math.min(255, Math.round(128 + 127 * v / WATER_VMAX)));
  for (let y = 0; y < ny; y++) {
    for (let x = 0; x < nx; x++) {
      let n = 0, w = 0, s = 0;
      for (let dy = 0; dy < r; dy++) {
        const cy = y * r + dy;
        if (cy >= f.h) break;
        for (let dx = 0; dx < r; dx++) {
          const cx = x * r + dx;
          if (cx >= f.w) break;
          n++;
          if (f.data[cy * f.w + cx] === WATER_ID) {
            w++;
            if (sh && !s && sheet((cx + 0.5) * f.cell, (cy + 0.5) * f.cell)) s = 1;
          }
        }
      }
      const i = 4 * (y * nx + x);
      d[i] = 128; d[i + 1] = 128; d[i + 2] = 0; d[i + 3] = n ? Math.round(255 * w / n) : 0;
      if (s) sh[y * nx + x] = 1;
      if (w && flow) {
        const v = flow((x + 0.5) * cell, (y + 0.5) * cell);
        if (v) { d[i] = enc(v[0]); d[i + 1] = enc(v[1]); }
      }
    }
    yield;
  }
  if (sh) {
    for (let y = 0; y < ny; y++) {
      for (let x = 0; x < nx; x++) {
        let s = 0;
        for (let dy = -1; dy <= 1 && !s; dy++) {
          for (let dx = -1; dx <= 1; dx++) {
            const X = x + dx, Y = y + dy;
            if (X >= 0 && Y >= 0 && X < nx && Y < ny && sh[Y * nx + X]) { s = 1; break; }
          }
        }
        if (s) d[4 * (y * nx + x) + 2] = 255;
      }
      yield;
    }
  }
  return { d, nx, ny, cell };
}

// ------------------------------------------------------------- water tiles
// WATER-G V_WATER (terrain.js _maskFor / variantKeepers): which terrain tiles
// compile the water term. Only those: a tile that meets no water keeps the
// program text it had before WATER-G. CONSERVATIVE — a tile any water path
// could colour gets the bit:
//  (1) the coarse grid: a block (4 water-texture cells, 32 m at 8 m) is
//      marked when it holds a class-254 coarse cell, then the marks are
//      dilated by one block — wider than every reach a coarse cell has: the
//      LINEAR uWater (1.5 water cells) + the class texels' bilinear (1 coarse
//      cell).
// V_GROUND needs no marks: every V_GROUND program carries the term (its 1 m
// class texels can hold water the coarse grid missed), behind uWaterOn.
// waterIn(box): any marked block under the box (a prefix sum, O(1)).
// R8d3: the marks are a STEP GENERATOR (waterBoxesSteps: the class grid scan,
// the dilation and the prefix sum, a step per WB_SCAN cells / WB_BOX blocks
// of rows; the live probe's one-shot was a 25-56 ms share of the class
// grid's caller task) run by ONE coalesced async build like the water
// texture's (_boxesBuild): setCoarseClasses bumps `gen` and returns at once,
// a running build stops at its next step once a newer grid came, the loop
// restarts on the LATEST grid. The marks swap (_wbx, _wbx.gen++) only at the
// end of the build that saw no newer request. Until then waterIn answers
// from the PREVIOUS marks (false before the first: the old "false until the
// class grid landed") — a WHEN change, never a value change: the same marks
// for the same grid, only later; terrain.update re-classifies on waterGen.
// waterBoxesIdle() = the running build.
const _wbx = { B: 0, nx: 0, ny: 0, m: null, sat: null, gen: 0 };
const _wbq = { f: null, gen: 0, run: null };
const WB_SCAN = 1 << 18, WB_BOX = 1 << 14;
function buildWaterBoxes(f) {
  _wbq.f = f;
  _wbq.gen++;
  if (!_wbq.run) _wbq.run = _boxesBuild();
}
export function waterBoxesIdle() { return _wbq.run || Promise.resolve(); }
export const waterBoxesBusy = () => !!_wbq.run;
// the marks waterIn answers from now (the gate reads them; do not mutate)
export function waterBoxesNow() { const { B, nx, ny, m, sat } = _wbx; return { B, nx, ny, m, sat }; }
async function _boxesBuild() {
  try {
    await slice();
    for (let g = -1; g !== _wbq.gen;) {
      g = _wbq.gen;
      const f = _wbq.f;
      if (!f || !f.data || !(f.w > 0) || !(f.h > 0)) continue;
      const live = () => g === _wbq.gen;
      const r = await runSteps(whileLive(waterBoxesSteps(f), live), 'water.boxes');
      if (r && live()) {
        Object.assign(_wbx, r);
        _wbx.gen++;
      }
    }
  } finally { _wbq.run = null; }
}
// the marks of class grid f in steps -> {B, nx, ny, m, sat}. Pure (the gate
// drains it against the pre-R8d3 one-shot, 942b64a buildWaterBoxes).
export function* waterBoxesSteps(f) {
  const r = Math.max(1, Math.round(WCFG.flow_res_m / f.cell));
  const B = 4 * r * f.cell, k = 4 * r;
  const nx = Math.ceil(f.w / k), ny = Math.ceil(f.h / k);
  const raw = new Uint8Array(nx * ny);
  const scanRows = Math.max(1, Math.floor(WB_SCAN / f.w)), boxRows = Math.max(1, Math.floor(WB_BOX / nx));
  for (let cy = 0; cy < f.h; cy++) {
    const row = cy * f.w, by = Math.floor(cy / k) * nx;
    for (let cx = 0; cx < f.w; cx++) if (f.data[row + cx] === WATER_ID) raw[by + Math.floor(cx / k)] = 1;
    if ((cy + 1) % scanRows === 0) yield;
  }
  const m = new Uint8Array(nx * ny);
  for (let y = 0; y < ny; y++) {
    for (let x = 0; x < nx; x++) {
      if (!raw[y * nx + x]) continue;
      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          const X = x + dx, Y = y + dy;
          if (X >= 0 && Y >= 0 && X < nx && Y < ny) m[Y * nx + X] = 1;
        }
      }
    }
    if ((y + 1) % boxRows === 0) yield;
  }
  const sat = yield* _satSteps(m, nx, ny, boxRows);
  return { B, nx, ny, m, sat };
}
function* _satSteps(m, nx, ny, rows) {
  const s = new Uint32Array((nx + 1) * (ny + 1));
  for (let y = 0; y < ny; y++) {
    let row = 0;
    for (let x = 0; x < nx; x++) {
      if (m[y * nx + x]) row++;
      s[(y + 1) * (nx + 1) + x + 1] = s[y * (nx + 1) + x + 1] + row;
    }
    if ((y + 1) % rows === 0) yield;
  }
  return s;
}
// does the world box [x0, x1) x [z0, z1) (m) meet water? False until the
// first marks swapped in (R8d3: the class grid landed AND its marks built).
export function waterIn(x0, z0, x1, z1) {
  const { B, nx, ny, sat } = _wbx;
  if (!sat) return false;
  const a = Math.max(0, Math.floor(x0 / B)), b = Math.min(nx, Math.ceil(x1 / B));
  const c = Math.max(0, Math.floor(z0 / B)), d = Math.min(ny, Math.ceil(z1 / B));
  if (a >= b || c >= d) return false;
  const W = nx + 1;
  return sat[d * W + b] - sat[c * W + b] - sat[d * W + a] + sat[c * W + a] > 0;
}
// ticks whenever the marks change (terrain.update re-classifies)
export function waterGen() { return _wbx.gen; }

// ------------------------------------------------------------- material arrays
// LAZY LAYERS (materials-lazy). One CompressedArrayTexture per kind (A, B)
// holds every layer's slice, allocated once from the manifest's NEUTRAL file
// (x1, a flat normal, cavity 1 in every slice — ring.js's optic taps read it
// unmasked). A layer's own two files are fetched only once a class, an
// elevation band, a path / way kind or a cliff host that maps to it can be on
// screen (gmLayersFor: the near window's 1 m class + ways tiles, the coarse
// grid over lazy_coarse_r tiles around the near tile, the far window's
// tiles, the optic ring's layers once the optic is used), and its mip chains
// go into its slice (layerUpdates); uMatMask bit L flips when both of its
// slices are in — until then every groundmat tap of L mixes to neutral, so a
// missing layer is exactly x1 and nothing ever draws an empty slice. Tier 2 =
// 1024², tier 1 = 512²; a device without a block format gets RGBA32 and tier
// 1 (memory). R8d6b: every upload is a compile-queue piece (q.steps, prio
// 'prepare'): the two neutral stacks ONE SLICE A PIECE (quiet window 4: a
// whole stack was one 159-248 ms compressedTexSubImage3D task), a landing's A
// and B slices two pieces; uMatOn flips with the first layer (until then the
// output IS the baked tile). Never throws.
// The bytes come through the asset queue as `bulk` (t43), each with the need
// rect of the tile it was first seen in (in view / under the target first),
// held from the Beam to the latch; KTX2Loader.parse is what its loadAsync
// does after the fetch. The program text never depends on what has landed.
const _lz = {
  need: new Map(),   // layer -> need rect (the tile it was first seen in; null = world-wide)
  req: new Set(),    // layers fetched or in flight
  mask: 0,           // layers whose slices hold their files
  landed: [],        // {L, tA, tB} transcoded, waiting for the upload ticks
  busy: false,
  go: null,          // set once the arrays exist: fetch every needed layer not yet asked
  pump: null,
  seen: new Set(),   // 'c|w|f tx,ty' tiles already scanned
  coarse: null, cbits: new Map(), center: '', at: null,
  optic: false,
  q: 2,              // the tier (loadGroundMaterials; the cliff layer is tier 2's)
};
export function loadGroundMaterials(tier, ktx2, renderer, blockFormat, q) {
  if (!(tier >= 1) || !ktx2) return Promise.resolve(false);
  if (NL > 32) { console.warn('[ground] materials: > 32 layers, uMatMask holds 32'); return Promise.resolve(false); }
  const dir = tier >= 2 && blockFormat ? 't2' : 't1';
  _lz.q = tier;
  // one file through the queue -> its transcoded texture; null = dropped by
  // the need score (asked again at the next need)
  const get = async (f, need) => {
    const r = await fetchAsset(`materials/${f.file}?v=${f.sha256.slice(0, 12)}`, { prio: 'bulk', as: 'arrayBuffer', status: true, need });
    const ab = r && r.body;
    if (!ab) { if (r && r.dropped) return null; throw new Error(`ground material: ${f.file}`); }
    return new Promise((res, rej) => ktx2.parse(ab, res, rej));
  };
  let arrA = null, arrB = null;
  // the pieces through the queue -> every one ran (one that throws ends its
  // queue job: the rest never run, and nothing may sample a half stack)
  const upAll = async (pieces, o) => {
    let n = 0;
    await q.steps(pieces.map((p) => ({ bytes: p.bytes, run: () => { p.run(); n++; } })), o);
    return n === pieces.length;
  };
  const fits = (t) => t.format === arrA.format && t.type === arrA.type && t.mipmaps.length === arrA.mipmaps.length
    && t.mipmaps.every((m, i) => m.width === arrA.mipmaps[i].width && m.data.length * NL === arrA.mipmaps[i].data.length);
  const fetchLayer = (L, rect) => {
    const l = MANIFEST.layers[L];
    _lz.req.add(L);
    const one = (kind) => { const f = l.files[`${dir}_${kind}`]; return get(f, { rect, m: null, bytes: f.bytes }); };
    Promise.all([one('A'), one('B')]).then(([tA, tB]) => {
      if (tA && tB && fits(tA) && fits(tB)) { _lz.landed.push({ L, tA, tB }); _lz.pump(); return; }
      if (tA) tA.dispose();
      if (tB) tB.dispose();
      if (!tA || !tB) _lz.req.delete(L);   // dropped: the next need asks again
      else console.warn(`[ground] material ${l.key}: format / mips differ from the neutral slice — x1`);
    }).catch((e) => console.warn(`[ground] material ${l.key} unavailable, x1:`, e && e.message));
  };
  _lz.pump = async () => {
    if (_lz.busy) return;
    _lz.busy = true;
    try {
      while (_lz.landed.length) {
        // ONE layer per pair of pieces: a burst of landings (the walk into a
        // new valley) never puts several slices into one piece
        const batch = _lz.landed.splice(0, 1), lb = arrA.mipmaps.reduce((k, m) => k + m.data.byteLength / NL, 0);
        if (!(await upAll([{ bytes: lb, run: () => gmPutSlices(renderer, arrA, batch.map((b) => [b.L, b.tA])) },
          { bytes: lb, run: () => gmPutSlices(renderer, arrB, batch.map((b) => [b.L, b.tB])) }], { label: 'gm.mat.layer', prio: 'prepare' }))) {
          console.warn(`[ground] material ${batch.map((b) => GM_LAYERS[b.L]).join(',')}: upload failed — x1`);
          continue;
        }
        for (const b of batch) _lz.mask |= 1 << b.L;
        GU.uMatMask.value = _lz.mask >>> 0;
        if (GU.uMatA.value !== arrA) {
          const oldA = GU.uMatA.value, oldB = GU.uMatB.value;
          GU.uMatA.value = arrA;
          GU.uMatB.value = arrB;
          GU.uMatOn.value = 1;
          oldA.dispose(); oldB.dispose();
        }
        console.info(`[ground] materials ${dir}: +${batch.map((b) => GM_LAYERS[b.L]).join(',')} (${_lz.req.size} of ${NL} asked)`);
      }
    } finally {
      _lz.busy = false;
    }
  };
  return (async () => {
    try {
      const nf = MANIFEST.neutral && MANIFEST.neutral[dir];
      if (!nf) throw new Error('the manifest names no neutral slice');
      const n0 = await get(nf, { rect: null, m: null, bytes: nf.bytes });
      if (!n0) throw new Error(`ground material: ${nf.file}`);
      // the two stacks in steps ('gm.stack'), swapped in together
      const sA = await runSteps(stackSteps(n0), 'gm.stack'), sB = await runSteps(stackSteps(n0), 'gm.stack');
      arrA = sA; arrB = sB;
      n0.dispose();
      if (!(await upAll([...stackPieces(renderer, arrA), ...stackPieces(renderer, arrB)], { label: 'gm.mat', prio: 'prepare' }))) {
        throw new Error('the neutral stacks did not upload');
      }
      _lz.go = () => { for (const [L, rect] of _lz.need) if (!_lz.req.has(L)) fetchLayer(L, rect); };
      _lz.go();
      console.info(`[ground] materials ${dir}: ${NL} slices ${arrA.mipmaps[0].width}², format ${arrA.format}; ${_lz.req.size} layers needed so far`);
      return true;
    } catch (e) {
      console.warn('[ground] materials unavailable, plain baked ground:', e && e.message);
      return false;
    }
  })();
}
// NL slices of one transcoded texture's mip chains (the neutral file).
// stackSteps = THE stack, a step per slice copy (R8 M6b: two stacks were one
// 62-77 ms task in the Map dwell); stackOf = its drain. Exported for
// scripts/check-materials.mjs.
export function stackOf(t0) {
  const it = stackSteps(t0);
  for (;;) { const r = it.next(); if (r.done) return r.value; }
}
export function* stackSteps(t0) {
  const mips = [];
  for (const m of t0.mipmaps) {
    const n = m.data.length, data = new m.data.constructor(n * NL);
    for (let k = 0; k < NL; k++) {
      if (k) yield;
      data.set(m.data, k * n);
    }
    mips.push({ data, width: m.width, height: m.height });
    yield;
  }
  const arr = new THREE.CompressedArrayTexture(mips, mips[0].width, mips[0].height, NL, t0.format, t0.type);
  arr.wrapS = arr.wrapT = THREE.RepeatWrapping;
  arr.minFilter = THREE.LinearMipmapLinearFilter;
  arr.magFilter = THREE.LinearFilter;
  arr.anisotropy = texAniso('mat', 4);   // texq.js: the class's row under TEX_HQ
  arr.generateMipmaps = false;
  arr.colorSpace = THREE.NoColorSpace;   // multipliers and normals, not colours
  // three r170 clears layerUpdates after mip 0 (the rest would re-upload the
  // whole stack): gmPutSlices clears them itself once every mip went up
  arr.clearLayerUpdates = () => {};
  arr.needsUpdate = true;
  return arr;
}
// R8d6b a neutral stack's first upload as compile-queue pieces: a block
// format one slice a piece (three's layerUpdates: the first piece allocates
// the storage, each uploads its slice's mips — together the whole-stack
// upload, slice for slice); the RGBA32 fallback is ONE piece (r170 uploads
// every slice of an uncompressed CompressedArrayTexture whatever
// layerUpdates hold). Nothing samples the stack before every piece ran (the
// uniforms swap at the first landing). Exported for scripts/check-materials.mjs.
export function stackPieces(renderer, arr) {
  const lb = arr.mipmaps.reduce((k, m) => k + m.data.byteLength / NL, 0);
  if (arr.format === THREE.RGBAFormat) return [{ bytes: lb * NL, run: () => renderer.initTexture(arr) }];
  return Array.from({ length: NL }, (_, L) => ({ bytes: lb, run: () => {
    arr.layerUpdates.add(L);
    arr.needsUpdate = true;
    renderer.initTexture(arr);
    arr.layerUpdates.clear();
  } }));
}
// copy each [layer, texture]'s mip chains into its slice, upload those slices
// only. A block format goes through three's layerUpdates; the RGBA32 fallback
// (no block format) cannot: r170's uncompressed CompressedArrayTexture branch
// ignores layerUpdates and re-uploads EVERY slice of every mip (t1: ~28 MB
// per kind per landing), so its slices go up here with texSubImage3D, depth
// 1, per mip (three's state cache binds; the CPU stack keeps every byte for a
// context restore). Exported for scripts/check-materials.mjs.
export function gmPutSlices(renderer, arr, list) {
  for (const [L, t] of list) {
    arr.mipmaps.forEach((m, i) => { const n = m.data.length / NL; m.data.set(t.mipmaps[i].data, L * n); });
    if (arr.format !== THREE.RGBAFormat) arr.layerUpdates.add(L);
    t.dispose();
  }
  if (arr.format === THREE.RGBAFormat) {
    const gl = renderer.getContext();
    renderer.state.bindTexture(gl.TEXTURE_2D_ARRAY, renderer.properties.get(arr).__webglTexture);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 4);
    gl.pixelStorei(gl.UNPACK_COLORSPACE_CONVERSION_WEBGL, gl.NONE);
    for (const [L] of list) {
      arr.mipmaps.forEach((m, i) => {
        const n = m.data.length / NL;
        gl.texSubImage3D(gl.TEXTURE_2D_ARRAY, i, 0, 0, L, m.width, m.height, 1, gl.RGBA, gl.UNSIGNED_BYTE, m.data.subarray(L * n, (L + 1) * n));
      });
    }
    return;
  }
  arr.needsUpdate = true;
  renderer.initTexture(arr);
  arr.layerUpdates.clear();
}

// The layers a set of class ids + ways look bytes (the ways field's P0.B:
// kind | hi << 4) reach on tier q, ascending: a class -> class_layer; 254
// water -> the gravel bed it shows through; 255 -> every band layer (the band
// is the fragment's elevation); a PATH-G kind -> its layer + its second
// (GM_P2); a built kind -> its layer + its side strip's (hi); a cliff host
// among them (a tier with the cliff) -> the cliff layer. groundGLSL's own
// mapping, in JS (scripts/check-materials.mjs row 9 holds them equal).
export function gmLayersFor(classes, bytes = [], q = 2) {
  const s = new Set();
  for (const c of classes) {
    if (c === WATER_ID) { if (GRAVEL >= 0) s.add(GRAVEL); }
    else if (c === 255) for (const L of MANIFEST.band_layers) s.add(L);
    else if (c >= 0 && c < 16 && CL[c] < NL) s.add(CL[c]);
  }
  for (const b of bytes) {
    const kind = b & 15, hi = b >> 4, L = PL[kind];
    if (!kind || L < 0) continue;
    s.add(L);
    if (PATH_KINDS.includes(kind)) { if (P2[kind] >= 0) s.add(P2[kind]); }
    else if (hi > 0 && PL[hi] >= 0) s.add(PL[hi]);
  }
  if (CLIFF_ON(q) && CLIFF.hosts.some((h) => s.has(h))) s.add(CLIFF.layer);
  return [...s].sort((a, b) => a - b);
}
// ring.js detailGlsl's evidence-picked layers (the optic ring's A taps)
const RING_KEYS = ['rock', 'scree', 'turf', 'snow'];
export const GM_RING_LAYERS = RING_KEYS.map((k) => GM_LAYER[k]).filter((L) => L >= 0);
// the distinct byte values among n bytes of `a` at `off` + `stride` * i
function bytesIn(a, n, stride = 1, off = 0) {
  const seen = new Uint8Array(256), out = [];
  for (let i = 0, j = off; i < n; i++, j += stride) {
    const v = a[j];
    if (!seen[v]) { seen[v] = 1; out.push(v); }
  }
  return out;
}
const tileM = () => _near.tpx * GU.uClassInfo.value.w;
const tileRect = (tx, ty) => { const m = tileM(); return [tx * m, ty * m, (tx + 1) * m, (ty + 1) * m]; };
// layers L are needed (rect: where first) -> fetched once the arrays exist
function gmNeed(layers, rect) {
  for (const L of layers) if (!_lz.need.has(L)) _lz.need.set(L, rect);
  if (_lz.go) _lz.go();
}
// a resident 1 m class tile (near or far window) / a ways tile, once each
function needClassTile(tx, ty, cls) {
  const k = `c${tx},${ty}`;
  if (_lz.seen.has(k)) return;
  _lz.seen.add(k);
  gmNeed(gmLayersFor(bytesIn(cls, cls.length), [], _lz.q), tileRect(tx, ty));
}
function needWaysTile(tx, ty, ways, T) {
  const k = `w${tx},${ty}`;
  if (_lz.seen.has(k)) return;
  _lz.seen.add(k);
  gmNeed(gmLayersFor([], bytesIn(ways, T * T, 4, 2), _lz.q), tileRect(tx, ty));
}
// the coarse grid's classes in classmap tile (tx, ty) (cached per tile)
function coarseTileClasses(tx, ty) {
  const f = _lz.coarse, k = tx + ',' + ty;
  if (!f) return [];
  if (_lz.cbits.has(k)) return _lz.cbits.get(k);
  const n = Math.max(1, Math.round(tileM() / f.cell));
  const x0 = tx * n, y0 = ty * n, x1 = Math.min(f.w, x0 + n), y1 = Math.min(f.h, y0 + n);
  const seen = new Uint8Array(256), out = [];
  for (let y = Math.max(0, y0); y < y1; y++) {
    for (let x = Math.max(0, x0), i = y * f.w + x; x < x1; x++, i++) {
      const v = f.data[i];
      if (!seen[v]) { seen[v] = 1; out.push(v); }
    }
  }
  _lz.cbits.set(k, out);
  return out;
}
// the near tile moved (or the coarse grid landed): the coarse grid's layers
// over the (2 lazy_coarse_r + 1)² tiles around it — the margin beyond the
// near window's own 1 m classes (the macro tap, a walk into the next tile)
function needAround(tx, ty) {
  if (!Number.isFinite(tx) || !Number.isFinite(ty)) return;
  const key = `${tx},${ty}`;
  _lz.at = [tx, ty];
  if (!_lz.coarse || key === _lz.center) return;
  _lz.center = key;
  const r = Math.max(0, Math.floor(Number(CFG.lazy_coarse_r) || 0));
  const list = [];
  for (let j = ty - r; j <= ty + r; j++) {
    for (let i = tx - r; i <= tx + r; i++) {
      const cls = coarseTileClasses(i, j);
      if (cls.length) list.push([Math.max(Math.abs(i - tx), Math.abs(j - ty)), i, j, cls]);
    }
  }
  list.sort((a, b) => a[0] - b[0]);   // the near tile's own first
  for (const [, i, j, cls] of list) gmNeed(gmLayersFor(cls, [], _lz.q), tileRect(i, j));
}
// the optic is in use: the optic ring's layers (main.js / the far window's
// update call it; idempotent)
export function noteGroundOptic() {
  if (_lz.optic) return;
  _lz.optic = true;
  gmNeed(GM_RING_LAYERS, null);
}

// ------------------------------------------------------------- near window
// The 3 x 3 classmap tiles surface.js keeps resident around the player, with
// their shade tiles, in one RGBA8 texture: tile (tx, ty) lives in slot
// (tx mod 3, ty mod 3) — a walk overwrites the slots it leaves behind. Each
// landing copies one 512² block (subUpload, in row bands: bandPieces), never
// the whole.
//
// WAYS-G: the ways field tiles (surface onWays: the two planes per tile) fill
// two more windows uNearW0 / uNearW1 the same way — RGBA8, LINEAR + repeat
// (the distances / phases are read bilinearly across slots; the 2 x 2
// footprint is slot-checked in the shader), the kind / code bytes by
// texelFetch. A slot's ways record (uNearWSlot) is its own: a ways tile may
// land before or after its class tile, and a tile whose ways never land
// reads no way. 2 x 1536² x 4 = 18.9 MB (they replace the PATH-G window and
// the drawn-strip mask: net one texture unit less).
// n RGBA pixels, every one `rgba` (R8d3: the window fills were a per-pixel
// loop, 2.36 M typed-array sets a window, 160-294 ms on the live probe):
// the first pixel byte by byte, then doubled with copyWithin (log2 n
// memcpys) — the same bytes, endian-independent.
export function fillRGBA(n, rgba) {
  const a = new Uint8Array(n * 4);
  if (!n) return a;
  a[0] = rgba[0]; a[1] = rgba[1]; a[2] = rgba[2]; a[3] = rgba[3];
  for (let k = 4; k < a.length; k *= 2) a.copyWithin(k, 0, Math.min(k, a.length - k));
  return a;
}
// R8d4: the attach IN STEPS (the live probe: ONE 174 ms task in the Map
// phase — the three fills, their uploads and the far window's). A step
// generator run by runSteps under 'gm.window', each step ONE fill or ONE
// upload (initTexture); drained (a gate, shaderprobe) it is the one-shot. The
// tile callbacks are wired at the first step; one that fires before the
// windows exist is queued and replayed in arrival order, one per step, after
// them — the same puts in the same order. Until then the 1 x 1 defaults (the
// same fill values, no slot) are bound.
export function attachNearWindow(renderer, surface, q) {
  return runSteps(nearWindowSteps(renderer, surface, q), 'gm.window');
}
// q: the compile queue (q.steps: a landing's row bands, R8d6b; a gate's drains them)
export function* nearWindowSteps(renderer, surface, q) {
  if (!surface || !surface.tileData) return;
  const T = _near.tpx, NP = 3 * T;
  // the ways windows: the bake's ways tiles (surface.js requires them) in
  // the encoding the GLSL constants decode — surface.ways.enc == WAYS_ENC,
  // anything else throws (the shader would misread every way)
  const wm = surface.ways;
  if (!wm || !wm.enc || !Object.keys(WAYS_ENC).every((k) => wm.enc[k] === WAYS_ENC[k])) {
    throw new Error(`world meta.surface.ways.enc ${JSON.stringify(wm && wm.enc)} != ${JSON.stringify(WAYS_ENC)}: rebuild this world (Build)`);
  }
  let win = null, winW0 = null, winW1 = null, wmask = null;
  // R8d6b: a landing's write goes in row bands (bandPieces) through q; the
  // slot record is set after its last band. Another tile's landing in that
  // slot clears the record first (the old texels are never read as the new
  // tile); the same tile again (its shade, the window callback) keeps it
  // until the new bytes are all in. A newer landing in a slot skips the
  // older one's remaining bands (gen) — the last call wins, as before.
  const gen = new Uint32Array(9), genW = new Uint32Array(9);
  const putWays = (tx, ty) => {
    const d = surface.tileData(tx, ty);
    if (!d) return;
    const s = ((tx % 3) + 3) % 3 + 3 * (((ty % 3) + 3) % 3), slot = GU.uNearWSlot.value[s], g = ++genW[s];
    if (!d.ways || d.ways.length !== T * T * 8) {
      if (slot.x === tx && slot.y === ty) slot.z = 0;
      return;
    }
    needWaysTile(tx, ty, d.ways, T);   // LAZY: its kinds' layers
    if (slot.x !== tx || slot.y !== ty) slot.z = 0;
    const x = (s % 3) * T, y = Math.floor(s / 3) * T, live = () => genW[s] === g;
    q.steps([...bandPieces(renderer, d.ways.subarray(0, T * T * 4), T, T, winW0, x, y, live, null),
      ...bandPieces(renderer, d.ways.subarray(T * T * 4), T, T, winW1, x, y, live, () => slot.set(tx, ty, 1, 0))],
    { label: 'gm.window.ways', prio: 'first' });
  };
  const put = (tx, ty) => {
    const d = surface.tileData(tx, ty);
    if (!d || !d.cls || d.cls.length !== T * T) return;
    needClassTile(tx, ty, d.cls);   // LAZY: its classes' layers
    if (surface.nearTile) needAround(...surface.nearTile());
    const px = new Uint8Array(T * T * 4), sh = d.shade && d.shade.length === T * T * 3 ? d.shade : null;
    for (let i = 0, j = 0; i < T * T; i++, j += 3) {
      px[4 * i] = d.cls[i];
      px[4 * i + 1] = sh ? sh[j] : 255;
      px[4 * i + 2] = sh ? sh[j + 1] : 128;
      px[4 * i + 3] = sh ? sh[j + 2] : 128;
    }
    const s = ((tx % 3) + 3) % 3 + 3 * (((ty % 3) + 3) % 3), slot = GU.uNearSlot.value[s], g = ++gen[s];
    if (slot.x !== tx || slot.y !== ty) slot.z = 0;
    // WATER-F: the slot's water mask before its class bands (the slot record
    // names the tile only after both); made at the first wet tile, then
    // every landing writes its slot (a dry tile: zeros over a wet one's)
    if (!wmask && d.cls.indexOf(WATER_ID) >= 0) { wmask = waterMaskTex(renderer, T); GU.uNearWM.value = wmask; GU.uNearWMOn.value = 1; }
    const wp = wmask ? waterMaskPieces(renderer, waterMaskMips(d.cls, T), T, wmask, s, () => gen[s] === g) : [];
    q.steps([...wp, ...bandPieces(renderer, px, T, T, win, (s % 3) * T, Math.floor(s / 3) * T, () => gen[s] === g,
      () => slot.set(tx, ty, 1, sh ? 1 : 0))], { label: 'gm.window', prio: 'first' });
  };
  const early = [];   // [fn, tx, ty] fired before the windows exist
  const on = (fn) => (tx, ty) => { if (winW1) fn(tx, ty); else early.push([fn, tx, ty]); };
  const both = (tx, ty) => { put(tx, ty); putWays(tx, ty); };
  surface.onTile(on(both));
  if (surface.onShade) surface.onShade(on(put));
  surface.onWays(on(putWays));
  if (surface.onWindow) surface.onWindow(on(both));
  const mkWin = (ip, linear) => {
    const t = dataTex(ip, NP, NP, THREE.RGBAFormat);
    if (linear) {
      t.minFilter = t.magFilter = THREE.LinearFilter;
      t.wrapS = t.wrapT = THREE.RepeatWrapping;
    }
    renderer.initTexture(t);
    return t;
  };
  let ip = fillRGBA(NP * NP, [255, 255, 128, 128]);
  yield;
  const w = mkWin(ip, false);
  yield;
  ip = fillRGBA(NP * NP, [255, 255, 0, 0]);
  yield;
  const w0 = mkWin(ip, true);
  yield;
  ip = fillRGBA(NP * NP, [255, 128, 0, 128]);
  yield;
  const w1 = mkWin(ip, true);
  ip = null;
  win = w; winW0 = w0; winW1 = w1;
  GU.uNear.value = win;
  GU.uNearW0.value = winW0; GU.uNearW1.value = winW1;
  while (early.length) {
    yield;
    const [fn, tx, ty] = early.shift();
    fn(tx, ty);
  }
}

// ------------------------------------------------------------- far window
// ROCK-F B. The near window ends ~1 tile from the player; a magnified view
// (the focus wedge: the optic, or the desktop / phone view zoomed past
// view_arm) brings the relight's reach (relight_end_m x the magnification)
// kilometres out. attachFarWindow keeps far_slots[tier] more 512 m tiles
// (class + shade, surface.ensureFar) in uFar: the tiles the wedge hits within
// that reach, outside the 3 x 3, nearest the apex first. A slot is written
// only once its shade tile landed; a freed slot is invalidated before its
// rows are reused. Returns { update(focus, mag) (per frame, after
// focus.update: re-picks on a wedge / reach / near-tile change only), done
// (the stepped upload's promise) }, or null
// (tier without far slots, no surface / shade tiles).
export function attachFarWindow(renderer, surface, tier, q) {
  const n = FAR_N(tier);
  if (!n || !surface || !surface.ensureFar || !surface.onFar || !surface.meta || !surface.meta.tiles) return null;
  if (!surface.meta.shade) return null;
  const T = _near.tpx, TM = surface.meta.tiles.tile_m, NX = surface.meta.tiles.nx, NY = surface.meta.tiles.ny;
  const idx = dataTex(new Uint8Array(NX * NY), NX, NY, THREE.RedFormat);
  const gen = new Uint32Array(n);   // R8d6b: a newer tile in slot i skips the older one's bands
  // R8d4: its fill and its upload are two steps ('gm.window'); until both
  // ran nothing is asked (update() waits) and the 1 x 1 defaults are bound
  let tex = null;
  const done = runSteps((function* () {
    const init = fillRGBA(T * T * n, [255, 255, 128, 128]);
    yield;
    const t = dataTex(init, T, T * n, THREE.RGBAFormat);
    renderer.initTexture(t);
    tex = t;
    GU.uFar.value = tex;
    GU.uFarIdx.value = idx;
  })(), 'gm.window');
  surface.onFar((i, tx, ty) => {
    if (i >= n || !tex) return;
    const id = idx.image.data, g = ++gen[i];
    for (let k = 0; k < id.length; k++) if (id[k] === i + 1) id[k] = 0;   // the slot's old tile
    idx.needsUpdate = true;
    if (tx === null) return;
    const d = surface.farData(i);
    if (!d || !d.cls || d.cls.length !== T * T || !d.shade || d.shade.length !== T * T * 3) return;
    needClassTile(tx, ty, d.cls);   // LAZY: a far tile the wedge magnifies
    const px = new Uint8Array(T * T * 4), sh = d.shade;
    for (let k = 0, j = 0; k < T * T; k++, j += 3) {
      px[4 * k] = d.cls[k]; px[4 * k + 1] = sh[j]; px[4 * k + 2] = sh[j + 1]; px[4 * k + 3] = sh[j + 2];
    }
    // its rows in bands (R8d6b); the index names the slot after the last
    q.steps(bandPieces(renderer, px, T, T, tex, 0, i * T, () => gen[i] === g,
      () => { id[ty * NX + tx] = i + 1; idx.needsUpdate = true; }), { label: 'gm.far', prio: 'prepare' });
  });
  const RE = Math.max(1, CFG.relight_end_m[tier] || 0), half = TM * Math.SQRT1_2;
  let key = '';
  return {
    slots: n,
    done,
    // mag: the pass magnification over the eye (the fades' divisor)
    update(focus, mag) {
      if (!tex) return;
      if (!focus || !focus.active) {
        if (key !== 'off') { key = 'off'; surface.ensureFar([], n); }
        return;
      }
      noteGroundOptic();   // LAZY: a magnified view draws the optic ring's layers
      const reach = RE * Math.max(1, mag || 1);
      const [cx, cy] = surface.nearTile();
      const k = `${focus.gen}|${Math.round(Math.log2(reach) * 4)}|${cx},${cy}`;
      if (k === key) return;
      key = k;
      const list = [];
      for (let ty = 0; ty < NY; ty++) {
        for (let tx = 0; tx < NX; tx++) {
          if (Math.abs(tx - cx) <= 1 && Math.abs(ty - cy) <= 1) continue;   // the near window's
          const x = (tx + 0.5) * TM, z = (ty + 0.5) * TM;
          if (!focus.hit(x, z, half, reach)) continue;
          list.push([Math.hypot(x - focus.ox, z - focus.oz), tx, ty]);
        }
      }
      list.sort((a, b) => a[0] - b[0]);
      surface.ensureFar(list.map((e) => [e[1], e[2]]), n);
    },
  };
}

// ------------------------------------------------------------- GLSL
const CL = Array.from({ length: 16 }, (_, c) => {
  const k = MANIFEST.class_layer[c];
  return k >= 0 ? k : NL;
});
// the class-weight accumulator acc[GM_NA + 1] (slot GM_NA = no layer) spans
// only the layers a class, an elevation band, a PATH-G kind or the cliff's
// hosts reach — the built-way layers (asphalt .. steps) never enter it (they
// join the top-two pick as two candidates, 1b), so 6 more layers did not
// grow every ground fragment's array (dynamic indexing on a 21-slot array
// cost +15..20 % per near-ground fragment, waysg/L logs/gmcost)
const NA = 1 + Math.max(0, ...Object.values(MANIFEST.class_layer), ...MANIFEST.band_layers,
  ...PATH_KINDS.map((k) => PL[k]), ...P2, ...(CLIFF ? CLIFF.hosts : []));
const SNOW = GM_LAYER.snow ?? -1;

// declarations + helpers (q: the quality tier — the far window's
// declarations are tier 2's). use: what the program draws — 'ground'
// (V_GROUND, the default), 'macro' (V_MACRO: the coarse class + one macro
// tap) or 'deck' (deckwaymat: the way look + hex layers + the light model);
// each takes only the functions it calls. rough: the S2 terms (V_GROUND only;
// terrain.js leaves them out where no fragment can be inside rough_end_m).
// No renderconfig / manifest number is interpolated (STABLE TEXT, GU).
export function groundDecl(q = 0, water = false, { use = 'ground', rough = true } = {}) {
  const FS = FAR_N(q);
  const G = use === 'ground', M = use === 'macro', D = use === 'deck';
  return `
#define GM_NL ${NL}
#define GM_NONE ${NL}
#define GM_NA ${NA}` + (G ? `
uniform int uGmTpx;
#define GM_TPX uGmTpx
#define GM_NP (3 * uGmTpx)` : '') + (GM_PROBE.teeth_line && G ? `
#define GM_TEETH_LINE` : '') + `
uniform float uGmInvM[GM_NL];
#define GM_INVM uGmInvM
uniform vec4 uGmFade[3];
uniform vec4 uGmMat;
uniform vec4 uGmMat2;` + (G ? `
uniform vec4 uGmRoughF[3];
uniform vec4 uGmSlope;
uniform vec4 uGmCliff;` : '') + (!D ? `
const int GM_CL[16] = int[16](${CL.join(', ')});
const int GM_BAND[4] = int[4](${MANIFEST.band_layers.join(', ')});` : '') + (!M ? `
const int GM_PL[${PL.length}] = int[${PL.length}](${PL.join(', ')});
const int GM_P2[${P2.length}] = int[${P2.length}](${P2.join(', ')});
const float GM_PSHARE[${PSHARE.length}] = float[${PSHARE.length}](${PSHARE.map(f4).join(', ')});` : '') + `
uniform highp sampler2DArray uMatA;
uniform highp sampler2DArray uMatB;
uniform float uMatOn;
// LAZY: bit L = layer L's slices hold its files (loadGroundMaterials); an
// unloaded layer reads neutral — x1, a flat normal, cavity 1 — exactly
uniform highp uint uMatMask;` + (G ? `
uniform highp sampler2D uNear;
uniform vec4 uNearSlot[9];
uniform highp sampler2D uNearW0;
uniform highp sampler2D uNearW1;
uniform vec4 uNearWSlot[9];` : '') + (G && W_ON(water) ? `
uniform highp sampler2D uNearWM;
uniform float uNearWMOn;` : '') + (!M ? `
uniform vec3 uWayAlb[16];` : '') + (!D ? `
uniform highp sampler2D uClassM;
uniform vec4 uClassInfo;
uniform vec4 uBands;` : '') + (!M ? (W_ON(water) ? `
#define GM_LIGHT` : '') + `
uniform vec3 uSunL;
uniform vec3 uSky;
uniform vec3 uGnd;
uniform vec3 uSunC;
struct GmS { vec4 a; vec2 n; float c; };` : '') + (G && K_ON() ? `
uniform highp sampler2D uRough;
uniform vec4 uRoughInfo;
// S4: layer L's measured contrast factor out of the k grid tap (1: not a
// measured layer), and the detail scaled by it (the mean stays x1)
bool gmKOn(int L) { return ${KL.filter((L) => L >= 0).map((L) => `L == ${L}`).join(' || ')}; }
float gmK(int L, vec2 k) { return ${KL.map((L, c) => L >= 0 ? `L == ${L} ? k.${'xy'[c]} : ` : '').join('')}1.0; }
void gmKS(inout GmS S, float k) {
  if (k != 1.0) {
    S.a.rgb = max(0.5 + k * (S.a.rgb - 0.5), vec3(0.02));
    S.c = max(0.5 + k * (S.c - 0.5), 0.02);
    S.n *= k;
  }
}
vec3 gmKD(vec3 D, float k) { return k != 1.0 ? max(1.0 + k * (D - 1.0), vec3(0.04)) : D; }` : '') + (G ? `
int gmMod(int a, int m) { int r = a - (a / m) * m; return r < 0 ? r + m : r; }
// is 1 m texel t inside a near slot that holds its tile? (sh = shade ok)
bool gmSlotOk(ivec2 t, out float sh) {
  ivec2 tile = ivec2(floor(vec2(t) / float(GM_TPX)));
  vec4 sl = uNearSlot[gmMod(tile.x, 3) + 3 * gmMod(tile.y, 3)];
  sh = sl.w;
  return sl.z > 0.5 && abs(sl.x - float(tile.x)) < 0.5 && abs(sl.y - float(tile.y)) < 0.5;
}
// does the ways window's slot of 1 m texel t hold t's tile's ways tile?
bool gmWSlotOk(ivec2 t) {
  ivec2 tile = ivec2(floor(vec2(t) / float(GM_TPX)));
  vec4 sl = uNearWSlot[gmMod(tile.x, 3) + 3 * gmMod(tile.y, 3)];
  return sl.z > 0.5 && abs(sl.x - float(tile.x)) < 0.5 && abs(sl.y - float(tile.y)) < 0.5;
}
` : '') + (!M ? `
${PATH_CROSS_GLSL}
${D ? WAY_BASE_GLSL : WAY_FEAT_GLSL}` : '') + (G ? `
// (WAYSG-L4b) the P1 bytes at 1 m tile position p (texel centres at
// integers; uv = the caller's own texture coordinate of p) for a phase code
// cb (sleepers / treads), from that code's taps only: a junction cell (code
// none: a zero phase vector, mask 255; a lane line: its dash) never bends
// the phase nor lifts the mask. All four 2 x 2 taps holding cb: the hardware
// bilinear, bit for bit the pre-L4b read. Fewer: a weighted least-squares
// plane over the 4 x 4 taps holding cb (tent weights) of the flight mask
// (R) and of the phase angle unwrapped about the heaviest 2 x 2 tap — both
// linear along a way (pathfield runs s on past the way's end), so the last
// risers / sleepers stay straight up to the junction (the renormalised
// 2 x 2 average reads the values at the present taps' centroid, a riser bent
// by up to half a cell). wT = the 2 x 2 weight share holding cb.
vec4 gmP1Code(vec2 p, vec2 uv, int cb, out float wT) {
  ivec2 a = ivec2(floor(p));
  vec2 f = p - vec2(a);
  vec4 w = vec4((1.0 - f.x) * (1.0 - f.y), f.x * (1.0 - f.y), (1.0 - f.x) * f.y, f.x * f.y);
  vec4 s = vec4(0.0);
  float wm = -1.0;
  vec2 v0 = vec2(1.0, 0.0);
  wT = 0.0;
  for (int k = 0; k < 4; k++) {
    ivec2 c = a + ivec2(k & 1, k >> 1);
    vec4 q = texelFetch(uNearW1, ivec2(gmMod(c.x, GM_NP), gmMod(c.y, GM_NP)), 0) * 255.0;
    if (abs(q.b - float(cb)) < 0.5) {
      s += w[k] * q; wT += w[k];
      if (w[k] > wm) { wm = w[k]; v0 = q.ga - ${f1(WAYS_ENC.p_off)}; }
    }
  }
  if (wT > 0.99999) return textureLod(uNearW1, uv, 0.0) * 255.0;
  if (wT <= 0.0) return vec4(255.0, ${f1(WAYS_ENC.p_off)}, float(cb), ${f1(WAYS_ENC.p_off)});
  // sums of the weighted normal equations, coordinates relative to p (the
  // intercept is the value at p)
  mat3 M = mat3(0.0);
  vec3 bR = vec3(0.0), bP = vec3(0.0);
  for (int j = -1; j <= 2; j++) for (int i = -1; i <= 2; i++) {
    ivec2 c = a + ivec2(i, j);
    vec4 q = texelFetch(uNearW1, ivec2(gmMod(c.x, GM_NP), gmMod(c.y, GM_NP)), 0) * 255.0;
    if (abs(q.b - float(cb)) >= 0.5) continue;
    vec2 d = vec2(c) - p;
    float tw = max(0.0, 2.0 - abs(d.x)) * max(0.0, 2.0 - abs(d.y));
    if (tw <= 0.0) continue;
    vec3 X = vec3(1.0, d);
    M += tw * outerProduct(X, X);
    vec2 v = q.ga - ${f1(WAYS_ENC.p_off)};
    bR += tw * q.r * X;
    bP += tw * atan(v0.x * v.y - v0.y * v.x, dot(v0, v)) * X;
  }
  if (abs(determinant(M)) < 1e-3) return s / wT;   // collinear taps: the share
  mat3 Mi = inverse(M);
  float th = atan(v0.y, v0.x) + (Mi * bP).x;
  return vec4(clamp((Mi * bR).x, 0.0, 255.0), ${f1(WAYS_ENC.p_off)} + ${f1(WAYS_ENC.p_scale)} * cos(th), float(cb), ${f1(WAYS_ENC.p_off)} + ${f1(WAYS_ENC.p_scale)} * sin(th));
}
vec4 gmNearAt(ivec2 t) { return texelFetch(uNear, ivec2(gmMod(t.x, GM_NP), gmMod(t.y, GM_NP)), 0); }` : '') + (G && FS ? `
// ROCK-F B far window: the slot holding 1 m texel t's tile (its shade
// landed), -1 none — one fetch of the tile index; its texel
uniform highp sampler2D uFar;
uniform highp sampler2D uFarIdx;
int gmFarSlot(ivec2 t) {
  ivec2 tile = ivec2(floor(vec2(t) / float(GM_TPX)));
  if (any(lessThan(tile, ivec2(0))) || any(greaterThanEqual(tile, textureSize(uFarIdx, 0)))) return -1;
  return int(texelFetch(uFarIdx, tile, 0).r * 255.0 + 0.5) - 1;
}
vec4 gmFarAt(ivec2 t, int i) {
  ivec2 tile = ivec2(floor(vec2(t) / float(GM_TPX)));
  return texelFetch(uFar, t - tile * GM_TPX + ivec2(0, i * GM_TPX), 0);
}` : '') + (!D ? `
int gmCoarseAt(ivec2 c) {
  c = clamp(c, ivec2(0), ivec2(uClassInfo.yz) - 1);
  return int(texelFetch(uClassM, c, 0).r * 255.0 + 0.5);
}
// class 255: elevation bands (band_layers), 160 m soft transitions
void gmBand(float y, out int a, out int b, out float f) {
  float zc = clamp((y - uBands.x) / 160.0 + 0.5, 0.0, 1.0) + clamp((y - uBands.y) / 160.0 + 0.5, 0.0, 1.0)
           + clamp((y - uBands.z) / 160.0 + 0.5, 0.0, 1.0);
  float i = min(floor(zc), 2.0);
  a = GM_BAND[int(i)]; b = GM_BAND[int(i) + 1]; f = zc - i;
}
` : '') + (G ? `
void gmAdd(inout float acc[GM_NA + 1], int c, float w, int ba, int bb, float bf) {
  if (c < 16) acc[min(GM_CL[c], GM_NA)] += w;
  else if (c == 255) { acc[ba] += w * (1.0 - bf); acc[bb] += w * bf; }
  else acc[GM_NA] += w;
}
// the top-two pick: candidate layer L of weight w (merged when it is m1 / m2)
void gmTop(inout int m1, inout float w1, inout int m2, inout float w2, int L, float w) {
  if (w <= 0.0) return;
  if (L == m1) w1 += w;
  else if (L == m2) {
    w2 += w;
    if (w2 > w1) { int t = m1; m1 = m2; m2 = t; float u = w1; w1 = w2; w2 = u; }
  } else if (w > w1) { m2 = m1; w2 = w1; m1 = L; w1 = w; }
  else if (w > w2) { m2 = L; w2 = w; }
}
// review F3: one tap of the ways field's 2 x 2 into the look-byte weights:
// the primary byte bA, a second bB, the kind-none weight wN (a kind-none way
// cell: byte 0 inside reach, R < 255 — the funicular); an empty tap (byte 0,
// R 255) takes no part, a third byte is dropped
void gmKw(int b, float r, float w, inout int bA, inout float wA, inout int bB, inout float wB, inout float wN) {
  if (b == 0) { if (r < 254.5) wN += w; }
  else if (b == bA) wA += w;
  else if (b == bB) wB += w;
  else if (bA < 0) { bA = b; wA = w; }
  else if (bB < 0) { bB = b; wB = w; }
}
` : '') + (!M ? `
GmS gmNeutral() { GmS o; o.a = vec4(0.5); o.n = vec2(0.0); o.c = 0.5; return o; }
` : '') + `
// 1 when layer L has landed (uMatMask), else 0: every tap mixes to neutral
float gmOn(int L) { return float((uMatMask >> uint(clamp(L, 0, GM_NL - 1))) & 1u); }
` + (G ? `
// nb: also tap B (normal + cavity) — only the relight reads them
GmS gmTap(int L, vec2 uv, vec2 gx, vec2 gy, bool nb) {
  vec3 c3 = vec3(uv, float(L));
  float on = gmOn(L);
  GmS o;
  o.a = mix(vec4(0.5), textureGrad(uMatA, c3, gx, gy), on);
  vec4 b = nb ? mix(vec4(0.5), textureGrad(uMatB, c3, gx, gy), on) : vec4(0.5);
  o.n = b.xy * 2.0 - 1.0;
  o.c = b.z;
  return o;
}
` : '') + (!M ? `
vec2 gmH2(ivec2 p) {
  vec2 q = vec2(p);
  return fract(sin(vec2(dot(q, vec2(127.1, 311.7)), dot(q, vec2(269.5, 183.3)))) * 43758.5453);
}
// hex tiling: 3 taps on a triangle grid, each rotated + offset by its vertex
// hash; normals rotated back; weights sharpened by height (contrast kept)
GmS gmHex(int L, vec2 uv, vec2 gx, vec2 gy, bool nb) {${GM_LITE() ? '\n  return gmTap(L, uv, gx, gy, nb);   // the ground lite: one tap' : ''}
  vec2 sk = vec2(uv.x - 0.57735027 * uv.y, 1.15470054 * uv.y) * 3.4641016;
  ivec2 id = ivec2(floor(sk));
  vec2 fr = sk - vec2(id);
  float tz = 1.0 - fr.x - fr.y;
  float s = step(0.0, -tz), s2 = 2.0 * s - 1.0;
  vec3 w = vec3(-tz * s2, s - fr.y * s2, s - fr.x * s2);
  int si = int(s);
  ivec2 v0 = id + ivec2(si, si), v1 = id + ivec2(si, 1 - si), v2 = id + ivec2(1 - si, si);
  vec4 A[3]; vec2 N[3]; float C[3];
  for (int k = 0; k < 3; k++) {
    ivec2 v = k == 0 ? v0 : (k == 1 ? v1 : v2);
    vec2 h = gmH2(v);
    float ang = h.x * 6.2831853;
    float c = cos(ang), sn = sin(ang);
    mat2 R = mat2(c, sn, -sn, c);
    vec2 q = vec2(v);
    vec2 cen = vec2(q.x + 0.5 * q.y, 0.8660254 * q.y) / 3.4641016;
    vec3 c3 = vec3(R * (uv - cen) + cen + fract(h * 7.31), float(L));
    A[k] = textureGrad(uMatA, c3, R * gx, R * gy);
    vec4 b = nb ? textureGrad(uMatB, c3, R * gx, R * gy) : vec4(0.5);
    N[k] = transpose(R) * (b.xy * 2.0 - 1.0);
    C[k] = b.z;
  }
  vec3 W = w * mix(vec3(1.0), 0.15 + vec3(A[0].a, A[1].a, A[2].a), uGmMat2.w);
  W = pow(W, vec3(7.0));
  W /= max(W.x + W.y + W.z, 1e-30);
  float on = gmOn(L);
  GmS o;
  o.a = mix(vec4(0.5), A[0] * W.x + A[1] * W.y + A[2] * W.z, on);
  o.n = (N[0] * W.x + N[1] * W.y + N[2] * W.z) * on;
  o.c = mix(0.5, C[0] * W.x + C[1] * W.y + C[2] * W.z, on);
  return o;
}
// detail normal (xz in the tangent frame T, B around N) -> world
vec3 gmPerturb(vec3 N, vec3 T, vec3 B, vec2 n) {
  vec3 t = normalize(T - N * dot(N, T)), b = normalize(B - N * dot(N, B));
  return normalize(t * n.x + N * sqrt(max(0.05, 1.0 - dot(n, n))) + b * n.y);
}
vec3 gmE(vec3 N, float vis, float cav) {
  return (uGnd + (uSky - uGnd) * (0.5 * N.y + 0.5)) * cav + uSunC * (vis * max(dot(N, uSunL), 0.0));
}
` : '') + (G && rough ? roughDecl(q) : '') + (water ? waterDecl() : '');
}

// S2 rough layers (scree / rock / cliff: renderconfig rough_layers), tier 2,
// near (rough_end_m): the height in A-alpha (x the manifest's height range)
// shades the layer — a parallax offset of its taps along the view ray and a
// self-shadow toward the bake's sun that scales ONLY the sun term of the
// relight (the gaps go dark, every block keeps a lit and a shadowed side).
// Terrain geometry never moves. Height probes go through gmTap (masked by
// gmOn: a layer not landed reads flat) on the hex frame of the fragment —
// each vertex tap's uv + rotation and its triangle weights^7 (gmHex's blend
// without the height bias); taps under 2 % weight are skipped. A flat height
// gives a zero offset and no shadow, exactly. GM_PROBE.rough_off compiles
// every S2 term out (the program text as before S2).
function roughDecl(q) {
  if (!ROUGH_ON(q)) return '';
  const RF = CFG.rough_shadow_f, NP = ROUGH_NP();
  const top = ROUGH.ids.filter((i) => !CLIFF || i !== CLIFF.layer);
  return `struct GmHx { vec2 u[3]; mat2 R[3]; vec3 W; };
uniform float uGmHR[GM_NL];
uniform float uGmRF[${RF.length}];
uniform vec4 uGmRough;
bool gmRough(int L) { return ${top.map((i) => `L == ${i}`).join(' || ') || 'false'}; }
// gmHex's vertices, rotations and triangle weights at uv (no taps)
GmHx gmHexFrame(vec2 uv) {
  vec2 sk = vec2(uv.x - 0.57735027 * uv.y, 1.15470054 * uv.y) * 3.4641016;
  ivec2 id = ivec2(floor(sk));
  vec2 fr = sk - vec2(id);
  float tz = 1.0 - fr.x - fr.y;
  float s = step(0.0, -tz), s2 = 2.0 * s - 1.0;
  vec3 w = vec3(-tz * s2, s - fr.y * s2, s - fr.x * s2);
  int si = int(s);
  ivec2 v0 = id + ivec2(si, si), v1 = id + ivec2(si, 1 - si), v2 = id + ivec2(1 - si, si);
  GmHx F;
  for (int k = 0; k < 3; k++) {
    ivec2 v = k == 0 ? v0 : (k == 1 ? v1 : v2);
    vec2 h = gmH2(v);
    float ang = h.x * 6.2831853;
    float c = cos(ang), sn = sin(ang);
    F.R[k] = mat2(c, sn, -sn, c);
    vec2 q = vec2(v);
    vec2 cen = vec2(q.x + 0.5 * q.y, 0.8660254 * q.y) / 3.4641016;
    F.u[k] = F.R[k] * (uv - cen) + cen + fract(h * 7.31);
  }
  vec3 G = pow(w, vec3(7.0));
  F.W = G / (G.x + G.y + G.z);
  return F;
}
// layer L's height (A-alpha) - hm at offset d (layer uv) through frame F
float gmHexH(int L, GmHx F, vec2 d, vec2 gx, vec2 gy, float hm) {
  float h = 0.0, s = 0.0;
  for (int k = 0; k < 3; k++) {
    if (F.W[k] < 0.02) continue;
    h += F.W[k] * (gmTap(L, F.u[k] + F.R[k] * d, F.R[k] * gx, F.R[k] * gy, false).a.a - hm);
    s += F.W[k];
  }
  return h / s;
}
// a in the tangent frame of a projection (T = +u, B = +v around N): (t, b, n)
vec3 gmTan(vec3 a, vec3 N, vec3 T, vec3 B) {
  vec3 t = T - N * dot(N, T), b = B - N * dot(N, B);
  t *= inversesqrt(max(dot(t, t), 1e-8));
  b *= inversesqrt(max(dot(b, b), 1e-8));
  return vec3(dot(a, t), dot(a, b), dot(a, N));
}
// the sun's occlusion (0 lit .. 1) over L's height around offset d0 of F,
// where the fragment's height is h0: l = the sun in the tangent frame, the
// ray rising l.n / |l.tb| per metre; one tap where it has risen uGmRF x the
// height range hr (the first n of them; at most rough_shadow_max_m out), full once
// the height stands rough_shadow_soft x hr over the ray
float gmShadow(int L, GmHx F, vec2 d0, float h0, vec3 l, float hr, float im, vec2 gx, vec2 gy, float hm, int n) {
  float lt = max(length(l.xy), 1e-4);
  float m = max(l.z / lt, 1e-3);
  vec2 dl = l.xy / lt * im;
  float o = 0.0;
  for (int j = 0; j < n; j++) {
    float r = min(uGmRF[j] * hr / m, uGmRough.x);
    o = max(o, ((gmHexH(L, F, d0 + dl * r, gx, gy, hm) - h0) * hr - m * r) / (uGmRough.y * hr));
  }
  return min(o, 1.0);
}
` + roughHex('gmRoughHex', NP, ROUGH_NS()) + (RHC(q) !== 'gmRoughHex' ? roughHex('gmRoughHexC', ROUGH_NPC(), ROUGH_NSC()) : '');
}
function roughHex(name, np, ns) {
  return `// layer L hex-tiled at uv, rough (np probes, ns shadow taps: gmRoughHex
// rough_parallax_probes + all, gmRoughHexC the cliff's): v / l = the view / the sun in
// the projection's tangent frame (gmTan), im = 1 / metres per tile, k = the
// near fade (scales the offset); occ = the sun occlusion (nb: relight on)
GmS ${name}(int L, vec2 uv, vec2 gx, vec2 gy, bool nb, vec3 v, vec3 l, float im, float k, out float occ) {
  GmHx F = gmHexFrame(uv);
  float hr = uGmHR[L];` + (np > 1 ? `
  // parallax: the view ray over the layer's mean plane (its last mip);
  // ${np} probes, the last one at the offset (the shadow's own height there)
  float hm = gmTap(L, vec2(0.5), vec2(1e4), vec2(1e4), false).a.a;
  vec2 o = v.xy / max(v.z, uGmRough.w) * (uGmRough.z * k * im);
  float t = 0.0, h = gmHexH(L, F, vec2(0.0), gx, gy, hm);
  for (int j = 1; j < ${np}; j++) {
    t = j == 1 ? h * hr : 0.5 * (t + h * hr);
    h = gmHexH(L, F, o * t, gx, gy, hm);
  }
  vec2 d = o * t;
  GmS S = gmHex(L, uv + d, gx, gy, nb);
  occ = nb ? gmShadow(L, F, d, h, l, hr, im, gx, gy, hm, ${ns}) : 0.0;` : `
  // no parallax: the fragment's own (blended) height is the shadow's h0
  GmS S = gmHex(L, uv, gx, gy, nb);
  occ = nb ? gmShadow(L, F, vec2(0.0), S.a.a, l, hr, im, gx, gy, 0.0, ${ns}) : 0.0;`) + `
  return S;
}
`;
}

// ------------------------------------------------------------- water GLSL
// WATER-G (header). The declarations + functions every water surface uses:
// the terrain (groundDecl / the bare tier-0 block) and the stream ribbons
// (sky.js streamMaterial). Light uniforms are shared with groundDecl (the
// GM_LIGHT guard); bind GU (bindGround) for their values.
//   wtrSlope(p, v, fw): the ripple slope (dh/dx, dh/dz) at world xz p,
//     advected by v (m/s) + each octave's drift, fw = the pixel footprint m;
//   wtrSurf(sl, P, vis, out F): what the surface itself sends to the eye —
//     the sky it mirrors (x F) + the sun glint; F the fresnel reflectance.
//   The water colour = bed x (1 - F) + wtrSurf.
// The look's numbers are GU's uWtr* (STABLE TEXT); GM_PROBE.water_flat
// (the ripple amplitude 0) stays a compile switch.
export function waterDecl() {
  if (W_OFF()) return '';
  const [S1, S2] = W_FLAT() ? ['0.0', '0.0'] : ['uWtrA.x', 'uWtrA.y'];
  return `
// ---- WATER-G procedural water (groundmat.js waterDecl)
#ifndef GM_LIGHT
#define GM_LIGHT
uniform vec3 uSunL;
uniform vec3 uSky;
uniform vec3 uGnd;
uniform vec3 uSunC;
#endif
uniform float uWT;
uniform highp sampler2D uWater;
uniform vec4 uWaterInfo;
uniform float uWaterOn;
uniform vec4 uWtrA;
uniform vec4 uWtrB;
uniform vec4 uWtrD;
uniform vec4 uWtrT;
uniform vec4 uWtrF;
uniform vec4 uWtrG;
uniform vec4 uWtrH;
vec2 wtrH2(vec2 p) {
  vec3 p3 = fract(vec3(p.xyx) * vec3(0.1031, 0.1030, 0.0973));
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.xx + p3.yz) * p3.zy) * 2.0 - 1.0;
}
// the analytic gradient of 2D gradient noise (quintic fade)
vec2 wtrGrad(vec2 p) {
  vec2 i = floor(p), f = p - i;
  vec2 u = f * f * f * (f * (f * 6.0 - 15.0) + 10.0);
  vec2 du = 30.0 * f * f * (f * (f - 2.0) + 1.0);
  vec2 ga = wtrH2(i), gb = wtrH2(i + vec2(1.0, 0.0)), gc = wtrH2(i + vec2(0.0, 1.0)), gd = wtrH2(i + vec2(1.0, 1.0));
  float va = dot(ga, f), vb = dot(gb, f - vec2(1.0, 0.0)), vc = dot(gc, f - vec2(0.0, 1.0)), vd = dot(gd, f - vec2(1.0, 1.0));
  return ga + u.x * (gb - ga) + u.y * (gc - ga) + u.x * u.y * (ga - gb - gc + gd)
       + du * (u.yx * (va - vb - vc + vd) + vec2(vb, vc) - va);
}
// one octave: gradient of n(R q K) w.r.t. q, over K (the slope amplitude
// multiplies it)
vec2 wtrOct(vec2 q, float K, mat2 R) { return transpose(R) * wtrGrad(R * (q * K)); }
vec2 wtrSlope(vec2 p, vec2 v, float fw) {
  float o1 = ${S1} * (1.0 - smoothstep(uWtrB.x, uWtrB.y, fw));
  float o2 = ${S2} * (1.0 - smoothstep(uWtrB.z, uWtrB.w, fw));
  if (o1 + o2 <= 0.0) return vec2(0.0);
  const mat2 R1 = mat2(0.8253, 0.5646, -0.5646, 0.8253), R2 = mat2(-0.5885, 0.8085, -0.8085, -0.5885);
  float ph = uWT * uWtrT.x;
  float p0 = fract(ph), p1 = fract(ph + 0.5);
  float k = abs(1.0 - 2.0 * p0);
  float kn = inversesqrt((1.0 - k) * (1.0 - k) + k * k);   // the cross-fade keeps the amplitude
  vec2 v1 = v + uWtrD.xy, v2 = v + uWtrD.zw;
  vec2 s = vec2(0.0);
  if (o1 > 0.0) s += o1 * mix(wtrOct(p - v1 * (p0 * uWtrT.y), uWtrA.z, R1),
                              wtrOct(p + vec2(7.31, 3.17) - v1 * (p1 * uWtrT.y), uWtrA.z, R1), k);
  if (o2 > 0.0) s += o2 * mix(wtrOct(p - v2 * (p0 * uWtrT.y), uWtrA.w, R2),
                              wtrOct(p + vec2(2.71, 5.43) - v2 * (p1 * uWtrT.y), uWtrA.w, R2), k);
  return s * kn;
}
vec3 wtrSurf(vec2 sl, vec3 P, float vis, out float F) {
  vec3 N = normalize(vec3(-sl.x, 1.0, -sl.y));
  vec3 V = normalize(cameraPosition - P);
  float m = 1.0 - clamp(dot(N, V), 0.0, 1.0), m2 = m * m;
  F = min(uWtrF.x + uWtrF.y * m2 * m2 * m, uWtrF.z);
  vec3 H = normalize(V + uSunL);
  float g = uWtrF.w * pow(max(dot(N, H), 0.0), uWtrG.x) * vis * step(0.0, uSunL.y);
  return uSky * (uWtrT.z * F) + uSunC * g;
}
`;
}

// The terrain's water block (header). kind: 'ground' (V_GROUND: expects ww,
// vis, kN, gdx, gdy, gP in scope), 'macro' (V_MACRO) / 'bare' (no ground
// material: tier 0) — both expect gP, gdx, gdy and take ww from uWater.
export function waterTerrainGLSL(kind) {
  if (W_OFF()) return '';
  const ground = kind === 'ground';
  const bed = ground && GRAVEL >= 0 && Number(WCFG.bed_k) > 0;
  return `
    // WATER-G: the water surface over class 254 (groundmat waterDecl)` + (ground ? `
    // (every V_GROUND program carries it: off until terrain.applyWater)
    ww *= uWaterOn;
    if (ww > 0.0) {
      vec4 gWq = textureLod(uWater, gP.xz * uWaterInfo.xy, 0.0);` : `
    vec4 gWq = textureLod(uWater, gP.xz * uWaterInfo.xy, 0.0);
    float ww = smoothstep(uWtrG.y, uWtrG.z, gWq.a);
    if (ww > 0.0) {`) + `
      vec2 gWv = (gWq.rg * 255.0 - 128.0) * ${f4(WATER_VMAX / 127)};
      // a sheet lake's bed (B): no surface here — sky.js draws its sheet
      float gSh = gWq.b;
      vec2 gSl = wtrSlope(gP.xz, gWv, max(length(gdx), length(gdy))) * (1.0 - gSh);
      vec3 gBed = vec3(1.0);` + (bed ? `
      if (kN > 0.001) {
        // the riverbed seen through the water: gravel, offset by the ripple
        GmS gSG = gmHex(${GRAVEL}, (gP.xz + gSl * uWtrT.w) * uWtrH.w, gdx * uWtrH.w, gdy * uWtrH.w, false);
        gBed = mix(vec3(1.0), gSG.a.rgb * 2.0, kN * uWtrG.w);
      }` : '') + `
      float gF;
      vec3 gSurf = wtrSurf(gSl, gP, ${ground ? 'vis' : '1.0'}, gF);
      vec3 gB = diffuseColor.rgb * gBed;
      diffuseColor.rgb = mix(diffuseColor.rgb, mix(gB * uWtrH.rgb * (1.0 - gF) + gSurf, gB, gSh), ww);
    }`;
}

// macro tap of layer L (A only), rotated off the near grid; x1 until L lands
function macroTap(L) {
  return `mix(vec3(1.0), textureGrad(uMatA, vec3(gMR * (gP.xz * gIM), float(${L})), gMR * (gdx * gIM), gMR * (gdy * gIM)).rgb * 2.0, gmOn(${L}))`;
}

// The V_GROUND block. Expects in scope: vWorldPos2, `vdD` (view distance,
// optic-scaled); multiplies diffuseColor.rgb.
// GM_MAP: the colour-tile hooks of groundGLSL / mapGLSL — three's `map` (the
// default: today's text byte for byte, tools/shaderprobe.mjs proves it) or
// the GROUND-M virtual texture's pool (client/src/gmtex.js VT_MAP)
export const GM_MAP = {
  guard: '#ifdef USE_MAP',
  uv: 'vMapUv',
  dx: 'dFdx(vMapUv)',
  dy: 'dFdy(vMapUv)',
  tap: (uv) => `texture2D(map, ${uv})`,
  grad: (uv, a, b) => `textureGrad(map, ${uv}, ${a}, ${b})`,
  texels: 'float(textureSize(map, 0).x)',
};

// rough: the S2 terms (groundDecl's rough: both or neither)
export function groundGLSL(q, water = false, M = GM_MAP, rough = true) {
  // the fades are uGmFade[q] (gmFill: tier 0 never draws V_GROUND, terrain
  // _levelBits; >= 1 m keeps smoothstep's edges ordered if it is ever
  // compiled there — the shader probe does)
  const FS = FAR_N(q), CL = CLIFF_ON(q);
  // S2 rough layers: on (top-down m1 / m2 via gmRough), the cliff too; their
  // fade uGmRoughF[q].xy; RLO = their relight clamp floor .z is on (0: the global one)
  const RO = rough && ROUGH_ON(q), RC = RO && ROUGH_CLIFF(q);
  const RLO = RO && !!Number(CFG.rough_relight_min[q]);
  // S4: the measured contrast factor (scree / rock: gK1 / gK2 of m1 / m2)
  const KO = K_ON(), KS = (S, k) => (KO ? `
      gmKS(${S}, ${k});` : '');
  // a top-down rough tap (S1 / S2: occlusion o, rough flag r) or the plain hex
  const hexOf = (S, m, i, o, r) => `if (kP > 0.001 && gmRough(${m})) { ${S} = gmRoughHex(${m}, gP.xz * ${i}, gdx * ${i}, gdy * ${i}, nbk, gTv, gTl, ${i}, kP, ${o}); ${r} = 1.0; }
        else ${S} = gmHex(${m}, gP.xz * ${i}, gdx * ${i}, gdy * ${i}, nbk);`;
  // WATER-G: the bilinear water share of the four class texels
  const WW = (c0, c1, c2, c3, w0, w1, w2, w3) => (!W_ON(water) ? '' : `
      ww += (${c0} == ${WATER_ID} ? ${w0} : 0.0) + (${c1} == ${WATER_ID} ? ${w1} : 0.0)
          + (${c2} == ${WATER_ID} ? ${w2} : 0.0) + (${c3} == ${WATER_ID} ? ${w3} : 0.0);`);
  // the near / far window's four texels -> class weights + vis + shade normal
  const win = (at) => `
      vec4 s0 = ${at('t0', 0)}, s1 = ${at('t0 + ivec2(1, 0)', 1)};
      vec4 s2 = ${at('t0 + ivec2(0, 1)', 2)}, s3 = ${at('t0 + ivec2(1, 1)', 3)};
      gmAdd(acc, int(s0.r * 255.0 + 0.5), wq.x, ba, bb, bf);
      gmAdd(acc, int(s1.r * 255.0 + 0.5), wq.y, ba, bb, bf);
      gmAdd(acc, int(s2.r * 255.0 + 0.5), wq.z, ba, bb, bf);
      gmAdd(acc, int(s3.r * 255.0 + 0.5), wq.w, ba, bb, bf);${WW(`int(s0.r * 255.0 + 0.5)`, `int(s1.r * 255.0 + 0.5)`, `int(s2.r * 255.0 + 0.5)`, `int(s3.r * 255.0 + 0.5)`, 'wq.x', 'wq.y', 'wq.z', 'wq.w')}
      vec3 sh = s0.gba * wq.x + s1.gba * wq.y + s2.gba * wq.z + s3.gba * wq.w;
      vis = sh.x;
      nb = sh.yz * 2.0 - 1.0;`;
  return `
  {
    vec3 gP = vWorldPos2;
    vec2 gdx = dFdx(gP.xz), gdy = dFdy(gP.xz);
    // WAYS-G: the pixel footprint (m) and the colour tile's texel (m) — the
    // features' antialiasing and the painter's coverage model (no derivative
    // inside a branch)
    float gFW = max(max(length(gdx), length(gdy)), 0.004);
${M.guard}
    float gTc = gFW / max(max(length(${M.dx}), length(${M.dy})) * ${M.texels}, 1e-6);
#else
    float gTc = 0.25;
#endif` + (q >= 2 ? `
    vec3 gNg = normalize(cross(dFdx(gP), dFdy(gP)));
    vec2 gwx = dFdx(vec2(gP.z, -gP.y)), gwy = dFdy(vec2(gP.z, -gP.y));
    vec2 gzx = dFdx(vec2(gP.x, -gP.y)), gzy = dFdy(vec2(gP.x, -gP.y));` : '') + `
    const mat2 gMR = mat2(0.8, 0.6, -0.6, 0.8);
    // 1. class weights -> top two layers
    float acc[GM_NA + 1];
    for (int k = 0; k <= GM_NA; k++) acc[k] = 0.0;
    int ba, bb; float bf;
    gmBand(gP.y, ba, bb, bf);
    vec2 tp = gP.xz / uClassInfo.w - 0.5;
    ivec2 t0 = ivec2(floor(tp));
    vec2 fr = tp - vec2(t0);
    vec4 wq = vec4((1.0 - fr.x) * (1.0 - fr.y), fr.x * (1.0 - fr.y), (1.0 - fr.x) * fr.y, fr.x * fr.y);
    float sh0, sh1, sh2, sh3;
    bool nearOk = gmSlotOk(t0, sh0) && gmSlotOk(t0 + ivec2(1, 0), sh1)
               && gmSlotOk(t0 + ivec2(0, 1), sh2) && gmSlotOk(t0 + ivec2(1, 1), sh3);
    float vis = 1.0, shOk = 0.0;
    vec2 nb = vec2(0.0);` + (!W_ON(water) ? '' : `
    float ww = 0.0;   // WATER-G: the bilinear share of class 254`) + `
` + (FS ? `
    // ROCK-F B: beyond the near window, a far slot's 1 m class + shade
    int fs0 = -1, fs1 = -1, fs2 = -1, fs3 = -1;
    if (!nearOk) {
      fs0 = gmFarSlot(t0);
      if (fs0 >= 0) { fs1 = gmFarSlot(t0 + ivec2(1, 0)); fs2 = gmFarSlot(t0 + ivec2(0, 1)); fs3 = gmFarSlot(t0 + ivec2(1, 1)); }
    }` : '') + `
    if (nearOk) {${win((t) => `gmNearAt(${t})`)}
      shOk = min(min(sh0, sh1), min(sh2, sh3));` + (!W_ON(water) || W_POINT() ? '' : `
      // WATER-F: past one texel of footprint the mask's box share (uNearWM:
      // the slot's mips of the same class bytes) — up to one texel the
      // bilinear share above, exactly; only where the filter's whole reach
      // lies in slots that hold its tiles (else the exact share)
      float gWt = smoothstep(1.0, 2.0, gFW / uClassInfo.w) * uNearWMOn;
      if (gWt > 0.0) {
        vec2 gWr = (abs(gdx) + abs(gdy)) * (2.0 / uClassInfo.w) + 1.0;
        float gWd;
        if (gmSlotOk(ivec2(floor(tp - gWr)), gWd) && gmSlotOk(ivec2(floor(tp + gWr)), gWd)
            && gmSlotOk(ivec2(floor(vec2(tp.x - gWr.x, tp.y + gWr.y))), gWd) && gmSlotOk(ivec2(floor(vec2(tp.x + gWr.x, tp.y - gWr.y))), gWd)) {
          float gWs = 1.0 / (uClassInfo.w * float(GM_NP));
          ww = mix(ww, textureGrad(uNearWM, (tp + 0.5) / float(GM_NP), gdx * gWs, gdy * gWs).r, gWt);
        }
      }`) + `
    }` + (FS ? ` else if (min(min(fs0, fs1), min(fs2, fs3)) >= 0) {${win((t, k) => `gmFarAt(${t}, fs${k})`)}
      shOk = 1.0;
    }` : '') + ` else {
      vec2 cp = gP.xz / uClassInfo.x - 0.5;
      ivec2 c0 = ivec2(floor(cp));
      vec2 cf = cp - vec2(c0);
      gmAdd(acc, gmCoarseAt(c0), (1.0 - cf.x) * (1.0 - cf.y), ba, bb, bf);
      gmAdd(acc, gmCoarseAt(c0 + ivec2(1, 0)), cf.x * (1.0 - cf.y), ba, bb, bf);
      gmAdd(acc, gmCoarseAt(c0 + ivec2(0, 1)), (1.0 - cf.x) * cf.y, ba, bb, bf);
      gmAdd(acc, gmCoarseAt(c0 + ivec2(1, 1)), cf.x * cf.y, ba, bb, bf);${WW('gmCoarseAt(c0)', 'gmCoarseAt(c0 + ivec2(1, 0))', 'gmCoarseAt(c0 + ivec2(0, 1))', 'gmCoarseAt(c0 + ivec2(1, 1))',
        '(1.0 - cf.x) * (1.0 - cf.y)', 'cf.x * (1.0 - cf.y)', '(1.0 - cf.x) * cf.y', 'cf.x * cf.y')}
    }
    // 1b. WAYS-G: the ways field hands its share of the class weights to the
    // layer(s) of its kind (PATH-G kinds: the cross profile gmPathCross;
    // built kinds: share 1, the side strip beyond the carriageway bound to
    // its kind's layer); a built kind's near features as a colour ratio gWF
    // over the baked colour, the stair risers' tilt gWN (texel-exact reads
    // and fixed offsets only: no derivatives here)
    float pw = 0.0, pnk = 1.0, pwM = 0.0;   // pwM: the built kinds' share (no macro tap)
    int wL1 = GM_NONE, wL2 = GM_NONE, wL3 = GM_NONE;
    float wW1 = 0.0, wW2 = 0.0, wW3 = 0.0;
    vec3 gWF = vec3(1.0);
    vec2 gWN = vec2(0.0);
    if (gmWSlotOk(t0) && gmWSlotOk(t0 + ivec2(1, 0)) && gmWSlotOk(t0 + ivec2(0, 1)) && gmWSlotOk(t0 + ivec2(1, 1))) {
      vec2 wuv = (tp + 0.5) / float(GM_NP);
      vec4 w0 = textureLod(uNearW0, wuv, 0.0) * 255.0;
      float dE = (w0.r - ${f1(WAYS_ENC.r_off)}) / ${f1(WAYS_ENC.r_scale)};
      if (dE < ${f4(Math.max(PEDGE.soft_m, WEDGE.soft_m))}) {
        ivec2 tc = ivec2(floor(tp + 0.5));
        ivec2 tw = ivec2(gmMod(tc.x, GM_NP), gmMod(tc.y, GM_NP));
        // review F3: the look byte is not the nearest cell's (a 1 m staircase
        // at a kind / sac / side change, and a kind-none cell nearest killed
        // the edge ramp) but the 2 x 2 taps' bilinear weights per byte (the
        // painter's _path_kinds blend): the primary byte draws in full x wA,
        // a second byte adds its layer weight x wB, kind none (a funicular
        // cell) only normalises — the way fades out where it meets one. All
        // four taps one byte: wA = 1, wB = wN = 0, bit for bit the old path.
        ivec2 tx1 = ivec2(gmMod(t0.x + 1, GM_NP), 0), ty1 = ivec2(0, gmMod(t0.y + 1, GM_NP));
        ivec2 tx0 = ivec2(gmMod(t0.x, GM_NP), 0), ty0 = ivec2(0, gmMod(t0.y, GM_NP));
        vec4 q0 = texelFetch(uNearW0, tx0 + ty0, 0) * 255.0, q1 = texelFetch(uNearW0, tx1 + ty0, 0) * 255.0;
        vec4 q2 = texelFetch(uNearW0, tx0 + ty1, 0) * 255.0, q3 = texelFetch(uNearW0, tx1 + ty1, 0) * 255.0;
        int bA = -1, bB = -1;
        float wA = 0.0, wB = 0.0, wN = 0.0;
        gmKw(int(q0.b + 0.5), q0.r, wq.x, bA, wA, bB, wB, wN);
        gmKw(int(q1.b + 0.5), q1.r, wq.y, bA, wA, bB, wB, wN);
        gmKw(int(q2.b + 0.5), q2.r, wq.z, bA, wA, bB, wB, wN);
        gmKw(int(q3.b + 0.5), q3.r, wq.w, bA, wA, bB, wB, wN);
        if (wB > wA) { int t = bA; bA = bB; bB = t; float u = wA; wA = wB; wB = u; }
        if (wB == 0.0 && wN == 0.0) wA = 1.0;
        else { float sw = 1.0 / max(wA + wB + wN, 1e-6); wA *= sw; wB *= sw; }` + (GM_PROBE.kind_nearest ? `
        // GM_PROBE.kind_nearest (shaderprobe's F3 red twin): the pre-F3 read,
        // the nearest cell's byte in full
        { vec4 qn = texelFetch(uNearW0, tw, 0) * 255.0; bA = int(qn.b + 0.5); wA = 1.0; bB = -1; wB = 0.0; wN = 0.0; }` : '') + `
        int look = max(bA, 0);
        int kind = look & ${(1 << WAYS_ENC.kind_bits) - 1}, hi = look >> ${WAYS_ENC.kind_bits};
        int pL = GM_PL[kind];
        // G signed (PATH-G AMENDMENT 2): bilinear is exact across the centre line
        float wt = (w0.g - ${f1(WAYS_ENC.g_off)}) / ${f1(WAYS_ENC.g_scale)};
        float pg = min(abs(wt), 1.0);
        // the second byte: its layer(s) x wB (no features); into acc, a built
        // layer beyond it into the third top-two candidate (gmTop)
        float pB = 0.0, rB = 0.0;
        int LB = -1, LB2 = -1;
        if (bB > 0) {
          int kB = bB & ${(1 << WAYS_ENC.kind_bits) - 1};
          LB = GM_PL[kB];
          if (LB >= 0 && (${PATH_KINDS.map((k) => `kB == ${k}`).join(' || ')})) {
            float nkB;
            pB = wB * gmPathCross(kB, bB >> ${WAYS_ENC.kind_bits}, pg, nkB, rB)
               * (1.0 - smoothstep(${f4(-PEDGE.in_m)}, ${f4(PEDGE.soft_m)}, dE));
            LB2 = GM_P2[kB];
          } else if (LB >= 0) {
            pB = wB * (1.0 - smoothstep(${f4(-WEDGE.in_m)}, ${f4(WEDGE.soft_m)}, dE));
            pwM = pB;
          }
        }
        float pwA = 0.0;
        if (pL >= 0 && (${PATH_KINDS.map((k) => `kind == ${k}`).join(' || ')})) {
          float pr;
          pwA = wA * gmPathCross(kind, hi, pg, pnk, pr)
             * (1.0 - smoothstep(${f4(-PEDGE.in_m)}, ${f4(PEDGE.soft_m)}, dE));
          pw = pwA + pB;
          for (int k = 0; k <= GM_NA; k++) acc[k] *= 1.0 - pw;
          int p2 = GM_P2[kind];
          if (p2 >= 0) { acc[p2] += pwA * pr; acc[pL] += pwA * (1.0 - pr); }
          else acc[pL] += pwA;
          // (D1-MARKS) a ground mark on a PATH-G kind (a cattle grid on a
          // track): the ratio over the look's mean tread (no painter albedo)
          float gWbP = max(gFW, 1.5 * gTc);
          if (uMatOn > 0.5 && gWbP > gFW * 1.0001) {
            int cbP = int(texelFetch(uNearW1, tw, 0).b * 255.0 + 0.5);
            int codeP = cbP & ${(1 << WAYS_ENC.code_bits) - 1};` + (GM_PROBE.drop_mark ? `
            if (codeP == ${GM_PROBE.drop_mark | 0}) codeP = 0;` : '') + `
            if (codeP >= 5) {
              vec4 w1P = textureLod(uNearW1, wuv, 0.0) * 255.0;
              float wTP = 1.0;
              bool phP = codeP != 6` + (GM_PROBE.phase_all4 ? ' && false' : '') + `;
              if (phP) w1P = gmP1Code(tp, wuv, cbP, wTP);
              if (phP ? wTP > 0.0 : abs(w1P.b - float(cbP)) < 0.02) {
                vec3 BkP = GM_PATHBK[kind];
                vec3 AsP = gmMark(BkP, codeP, cbP >> ${WAYS_ENC.code_bits}, w1P, gFW, gFW);
                vec3 AbP = gmMark(BkP, codeP, cbP >> ${WAYS_ENC.code_bits}, w1P, gWbP, gTc);
                gWF = mix(vec3(1.0), clamp(AsP / max(AbP, vec3(1e-4)), vec3(0.2), vec3(5.0)), pwA);
              }
            }
          }
        } else if (pL >= 0) {
          pwA = wA * (1.0 - smoothstep(${f4(-WEDGE.in_m)}, ${f4(WEDGE.soft_m)}, dE));
          pw = pwA + pB;
          pwM += pwA;
          // the side strip (sidewalk / apron, kind hi) beyond the carriageway bound
          int sL = hi > 0 ? GM_PL[hi] : -1;
          float dS = (w0.a - ${f1(WAYS_ENC.a_off)}) / ${f1(WAYS_ENC.a_scale)};
          float sOk = (sL >= 0 && w0.a > 0.5) ? 1.0 : 0.0;
          float ws = sOk * clamp(0.5 + dS / gFW, 0.0, 1.0);
          for (int k = 0; k <= GM_NA; k++) acc[k] *= 1.0 - pw;
          // a built layer beyond the accumulator is a top-two candidate (gmTop)
          if (pL < GM_NA) acc[pL] += pwA * (1.0 - ws); else { wL1 = pL; wW1 = pwA * (1.0 - ws); }
          if (sL >= 0) { if (sL < GM_NA) acc[sL] += pwA * ws; else { wL2 = sL; wW2 = pwA * ws; } }
          // near features: sharp (footprint gFW) over the painter's model
          // (footprint gWb: its texel's box x the bilinear magnification);
          // equal once the footprint reaches the texel -> ratio 1
          vec3 Bk = uWayAlb[kind];
          float gWb = max(gFW, 1.5 * gTc);
          if (uMatOn > 0.5 && gWb > gFW * 1.0001 && dot(Bk, vec3(1.0)) > 1e-4) {
            vec3 Ash = Bk, Abk = Bk;
            if (sOk > 0.5) {
              vec3 Bh = uWayAlb[hi];
              if (dot(Bh, vec3(1.0)) < 1e-4) Bh = Bk;
              Ash = mix(mix(Bk, Bh, clamp(0.5 + dS / gFW, 0.0, 1.0)), GM_WKERB, gmCov(dS, 0.0, GM_WKERB_M, gFW));
              Abk = mix(mix(Bk, Bh, clamp(0.5 + dS / gWb, 0.0, 1.0)), GM_WKERB, gmCov(dS, 0.0, GM_WKERB_M, gWb));
            }
            int cb = int(texelFetch(uNearW1, tw, 0).b * 255.0 + 0.5);
            vec4 w1 = textureLod(uNearW1, wuv, 0.0) * 255.0;
            int code = cb & ${(1 << WAYS_ENC.code_bits) - 1};` + (GM_PROBE.drop_mark ? `
            if (code == ${GM_PROBE.drop_mark | 0}) code = 0;   // GM_PROBE.drop_mark: shaderprobe's red twin (a decoder without the code)` : '') + `
            float K = 2.0 * float(cb >> ${WAYS_ENC.code_bits});
            // a P1 feature: lane lines only where the four taps share the
            // code byte; a phase code (sleepers / treads, WAYSG-L4b) reads its
            // own taps (gmP1Code) up to the way's end — the all-four rule cut
            // the risers on the 1 m grid where a flight meets another way and
            // a neighbour's bytes in the gradient taps bent the riser normal
            // (chevrons on a diagonal flight)
            float wT1 = 1.0;
            // (GM_PROBE.phase_all4, shaderprobe's red twin: the pre-L4b read)
            // (D1-MARKS) the zebra / grid phases too (5, 7); an xline (6) is
            // no phase: its four taps share the byte
            bool phc = code >= 3 && code != 6` + (GM_PROBE.phase_all4 ? ' && false' : '') + `;
            if (phc) w1 = gmP1Code(tp, wuv, cb, wT1);
            if (code != 0 && (phc ? wT1 > 0.0 : abs(w1.b - float(cb)) < 0.02)) {
              if (code <= 2) {
                // lane lines (1 centre, 2 lane): distance + dash (gaps < 0)
                float dL = (w1.r - ${f1(WAYS_ENC.l_off)}) / ${f1(WAYS_ENC.l_scale)};
                float dD = (w1.g - ${f1(WAYS_ENC.d_off)}) / ${f1(WAYS_ENC.d_scale)};
                float cs = gmCov(dL, -0.5 * GM_WLINE_M, 0.5 * GM_WLINE_M, gFW) * clamp(0.5 + dD / gFW, 0.0, 1.0);
                float cbk = gmCov(dL, -0.5 * GM_WLINE_M, 0.5 * GM_WLINE_M, gWb) * clamp(0.5 + dD / gWb, 0.0, 1.0);
                Ash = mix(Ash, GM_WLINE, cs); Abk = mix(Abk, GM_WLINE, cbk);
              } else if (code >= 5) {
                // (D1-MARKS) a ground mark: zebra bars / give-way / stop line / cattle grid
                Ash = gmMark(Ash, code, cb >> ${WAYS_ENC.code_bits}, w1, gFW, gFW);
                Abk = gmMark(Abk, code, cb >> ${WAYS_ENC.code_bits}, w1, gWb, gTc);
              } else {
                // sleepers (3) / treads (4): the phase, and its gradient from
                // two taps 0.5 m east / south (the uphill direction, |grad t|)
                float ph = gmPhase(w1, K);
                float wTx, wTz;
                vec2 uvx = wuv + vec2(0.5 / float(GM_NP), 0.0), uvz = wuv + vec2(0.0, 0.5 / float(GM_NP));
                vec4 w1x = phc ? gmP1Code(tp + vec2(0.5, 0.0), uvx, cb, wTx) : textureLod(uNearW1, uvx, 0.0) * 255.0;
                vec4 w1z = phc ? gmP1Code(tp + vec2(0.0, 0.5), uvz, cb, wTz) : textureLod(uNearW1, uvz, 0.0) * 255.0;
                vec2 gph = vec2(mod(gmPhase(w1x, K) - ph + 0.5 * K, K) - 0.5 * K,
                                mod(gmPhase(w1z, K) - ph + 0.5 * K, K) - 0.5 * K) * 2.0;
                if (code == 3) {
                  const float pitch = ${f4(WB.sleeper_pitch_m)};
                  float x = (ph - floor(ph + 0.5)) * pitch;
                  float tx = (textureLod(uNearW0, wuv + vec2(0.5 / float(GM_NP), 0.0), 0.0).g * 255.0 - ${f1(WAYS_ENC.g_off)}) / ${f1(WAYS_ENC.g_scale)};
                  float tz = (textureLod(uNearW0, wuv + vec2(0.0, 0.5 / float(GM_NP)), 0.0).g * 255.0 - ${f1(WAYS_ENC.g_off)}) / ${f1(WAYS_ENC.g_scale)};
                  float o = wt / max(length(vec2(tx - wt, tz - wt) * 2.0), 0.02);
                  float ks = gmKeep(pitch, gFW), kb = gmKeep(pitch, gTc);
                  float mean = ${f4(WB.sleeper_w_m / WB.sleeper_pitch_m)};
                  float cs = (ks * gmCov(x, ${f4(-WB.sleeper_w_m / 2)}, ${f4(WB.sleeper_w_m / 2)}, gFW) + (1.0 - ks) * mean)
                           * gmCov(o, ${f4(-WB.sleeper_len_m / 2)}, ${f4(WB.sleeper_len_m / 2)}, gFW);
                  float cbk = (kb * gmCov(x, ${f4(-WB.sleeper_w_m / 2)}, ${f4(WB.sleeper_w_m / 2)}, gWb) + (1.0 - kb) * mean)
                            * gmCov(o, ${f4(-WB.sleeper_len_m / 2)}, ${f4(WB.sleeper_len_m / 2)}, gWb);
                  Ash = mix(Ash, GM_WSLEEPER, cs); Abk = mix(Abk, GM_WSLEEPER, cbk);
                } else if (code == 4) {
                  float pitch = ${f4(WS.phase_period_m)} / max(K, 1.0);
                  float x = fract(ph) * pitch;   // 0 at riser k, uphill to the next
                  float fm = (w1.r - ${f1(WAYS_ENC.d_off)}) / ${f1(WAYS_ENC.d_scale)};
                  float hh = max(0.03, 0.5 * gTc);
                  float wf = smoothstep(-hh, hh, fm);
                  const float nos = ${f4(WS.nosing_m)}, rb = ${f4(0.5 * WS.riser_m)};
                  float ks = gmKeep(pitch, gFW), kb = gmKeep(pitch, gTc);
                  float ns = ks * (gmCov(x, 0.0, nos, gFW) + gmCov(x - pitch, 0.0, nos, gFW)) + (1.0 - ks) * nos / pitch;
                  float rs = ks * (gmCov(x, pitch - rb, pitch, gFW) + gmCov(x + pitch, pitch - rb, pitch, gFW)) + (1.0 - ks) * rb / pitch;
                  float nb2 = kb * (gmCov(x, 0.0, nos, gWb) + gmCov(x - pitch, 0.0, nos, gWb)) + (1.0 - kb) * nos / pitch;
                  float rb2 = kb * (gmCov(x, pitch - rb, pitch, gWb) + gmCov(x + pitch, pitch - rb, pitch, gWb)) + (1.0 - kb) * rb / pitch;
                  Ash = mix(mix(Ash, GM_WNOSING, wf * ns), GM_WRISER, wf * rs);
                  Abk = mix(mix(Abk, GM_WNOSING, wf * nb2), GM_WRISER, wf * rb2);
                  // the riser face looks DOWN the flight: its normal tilts
                  // against the phase gradient (relight)
                  float lg = length(gph);
                  if (lg > 1e-3) gWN = -gph / lg * (0.8 * wf * ks * (rs - (1.0 - ks) * rb / pitch)) * wA;
                }
              }
            }
            gWF = mix(vec3(1.0), clamp(Ash / max(Abk, vec3(1e-4)), vec3(0.2), vec3(5.0)), pwA);
          }
        } else pw = pB;
        // the second byte's layers (acc already scaled by 1 - pw above, or
        // here when the primary has no layer)
        if (pL < 0) for (int k = 0; k <= GM_NA; k++) acc[k] *= 1.0 - pw;
        if (LB >= 0 && pB > 0.0) {
          if (LB2 >= 0) { acc[LB2] += pB * rB; acc[LB] += pB * (1.0 - rB); }
          else if (LB < GM_NA) acc[LB] += pB;
          else { wL3 = LB; wW3 = pB; }
        }
      }
    }
    int m1 = GM_NONE, m2 = GM_NONE;
    float w1 = -1.0, w2 = -1.0;
    for (int k = 0; k <= GM_NA; k++) {
      float w = acc[k];
      int L = k == GM_NA ? GM_NONE : k;
      if (w > w1) { m2 = m1; w2 = w1; m1 = L; w1 = w; } else if (w > w2) { m2 = L; w2 = w; }
    }
    gmTop(m1, w1, m2, w2, wL1, wW1);
    gmTop(m1, w1, m2, w2, wL2, wW2);
    gmTop(m1, w1, m2, w2, wL3, wW3);
    float wb = w2 > 0.0 ? w2 / (w1 + w2) : 0.0;
    float gOn = uMatOn;
    float kN = ${GM_LITE() ? '0.0' : `(1.0 - smoothstep(uGmFade[${q}].x, uGmFade[${q}].y, vdD)) * gOn`};
    float kR = ${RELIGHT_ON() ? `(1.0 - smoothstep(uGmFade[${q}].z, uGmFade[${q}].w, vdD)) * gOn * shOk` : '0.0'};
    // ROAD-MAT 3: no macro tap on a built way — the scan at macro_k x its
    // period paints ~1 m "stones" (soft olive / purple blotches on sett);
    // natural ground and PATH-G kinds keep it
    float kM = ${GM_LITE() ? '0.0' : 'smoothstep(8.0, 40.0, vdD) * uGmMat.x * gOn * (1.0 - min(pwM, 1.0))'};` + (RO ? `
    // S2: the rough layers' near fade (parallax + self-shadow)
    float kP = (1.0 - smoothstep(uGmRoughF[${q}].x, uGmRoughF[${q}].y, vdD)) * gOn;` : '') + (CL ? `
    // 6. ROCK-F C: the cliff share — the host layers' weight x the slope
    // ramp; cwx blends the two side projections across the diagonal
    float kC = (${CLIFF.hosts.map((h) => `acc[${h}]`).join(' + ')})
             * (1.0 - smoothstep(uGmSlope.x, uGmSlope.y, abs(gNg.y))) * gOn;
    float cwx = smoothstep(-0.08, 0.08, abs(gNg.x) - abs(gNg.z));` : '') + (RC ? `
    // S2: a full cliff share near takes the cliff alone (m1 / m2 would mix out)
    bool gCl = kP > 0.001 && kC >= 0.999;
    if (gCl) kC = 1.0;` : '') + `
` + (KO ? `
    // 8. S4: the k grid, one tap, only where m1 / m2 is a measured layer
    float gK1 = 1.0, gK2 = 1.0;
    if (uRoughInfo.z > 0.5${RC ? ' && !gCl' : ''} && (gmKOn(m1) || gmKOn(m2))) {
      vec2 gk = ${f4(GROUND_K[0])} + textureLod(uRough, gP.xz * uRoughInfo.xy, 0.0).rg * ${f4(GROUND_K[1] - GROUND_K[0])};
      gk = mix(gk, vec2(1.0), lessThan(abs(gk - 1.0), vec2(${KSNAP.toFixed(6)})));
      gK1 = gmK(m1, gk);
      gK2 = gmK(m2, gk);
    }` : '') + `
    vec3 gD = vec3(1.0), gF = vec3(1.0);
    if (kN > 0.001 || kR > 0.001) {
      // 2./3. the two layers, hex-tiled, height-blended
      bool nbk = kR > 0.001;
      float i1 = GM_INVM[min(m1, GM_NL - 1)], i2 = GM_INVM[min(m2, GM_NL - 1)];
      // (no ?: on structs: ESSL / ANGLE reject it)
      GmS S1 = gmNeutral(), S2 = gmNeutral();` + (RO ? `
      // S2: each layer's sun occlusion (0 = lit) and rough flag (1: the rough
      // path, the clamp floor RLO); the view and the sun in the top-down frame
      // (u = +x, v = +z) over the baked normal (never gNg: a screen-derivative
      // normal can come out NaN, today's clamps swallow it, a tap offset not)
      float gO1 = 0.0, gO2 = 0.0, gO = 0.0, gH1 = 0.0, gH2 = 0.0, gH = 0.0;
      vec3 gV = normalize(cameraPosition - gP), gNs = vec3(nb.x, sqrt(max(0.0, 1.0 - dot(nb, nb))), nb.y);
      vec3 gTv = vec3(0.0, 0.0, 1.0), gTl = gTv;
      if (kP > 0.001) {
        gTv = gmTan(gV, gNs, vec3(1.0, 0.0, 0.0), vec3(0.0, 0.0, 1.0));
        gTl = gmTan(uSunL, gNs, vec3(1.0, 0.0, 0.0), vec3(0.0, 0.0, 1.0));
      }
      if (m1 != GM_NONE${RC ? ' && !gCl' : ''}) {
        ${hexOf('S1', 'm1', 'i1', 'gO1', 'gH1')}
      }
      if (m2 != GM_NONE && wb >= 0.001${RC ? ' && !gCl' : ''}) {
        ${hexOf('S2', 'm2', 'i2', 'gO2', 'gH2')}
      }` : `
      if (m1 != GM_NONE) S1 = gmHex(m1, gP.xz * i1, gdx * i1, gdy * i1, nbk);
      if (m2 != GM_NONE && wb >= 0.001) S2 = ${q >= 2 ? 'gmHex' : 'gmTap'}(m2, gP.xz * i2, gdx * i2, gdy * i2, nbk);`) + `
` + KS('S1', 'gK1') + KS('S2', 'gK2') + `
      float h1 = S1.a.a + (1.0 - wb), h2 = S2.a.a + wb;
      float hm = max(h1, h2) - uGmMat2.z;
      float b1 = max(h1 - hm, 0.0), b2 = max(h2 - hm, 0.0);
      float wh = wb < 0.001 ? 0.0 : b2 / max(b1 + b2, 1e-5);` + (RO ? `
      gO = mix(gO1, gO2, wh);
      gH = mix(gH1, gH2, wh);` : '') + `
      vec3 Dn = mix(S1.a.rgb, S2.a.rgb, wh) * 2.0;
      // (PATH-G: the kind's cross profile scales the normal, gmPathCross)
      vec2 dn = mix(S1.n, S2.n, wh) * (uGmMat.w * mix(1.0, pnk, pw)) + gWN;
      float cav = mix(S1.c, S2.c, wh) * 2.0;` + (q >= 2 ? `
      // biplanar on steep faces: the dominant wall projection of m1
      float wall = m1 == GM_NONE ? 0.0 : smoothstep(0.5, 0.75, 1.0 - abs(gNg.y))${CL ? ' * (1.0 - kC)' : ''};
      bool xf = abs(gNg.x) > abs(gNg.z);
      vec2 dnW = vec2(0.0);
      if (wall > 0.001) {` + (RO ? `
        gO *= 1.0 - wall;
        gH *= 1.0 - wall;` : '') + `
        GmS SW;
        if (xf) SW = gmTap(m1, vec2(gP.z, -gP.y) * i1, gwx * i1, gwy * i1, nbk);
        else SW = gmTap(m1, vec2(gP.x, -gP.y) * i1, gzx * i1, gzy * i1, nbk);` + KS('SW', 'gK1') + `
        Dn = mix(Dn, SW.a.rgb * 2.0, wall);
        dnW = SW.n * uGmMat.w;
        cav = mix(cav, SW.c * 2.0, wall);
      }` : '') + (CL ? `
      // the cliff scan, hex-tiled, side-projected (x-facing: (z, -y); z-facing: (x, -y))
      vec2 dnCX = vec2(0.0), dnCZ = vec2(0.0);
      if (kC > 0.001) {
        float iC = uGmCliff.x;
        GmS CX = gmNeutral(), CZ = gmNeutral();` + (RC ? `
        // S2: rough in the side projections (x-facing: u = +z, z-facing:
        // u = +x; v = -y)
        float gOX = 0.0, gOZ = 0.0, gHC = 0.0;
        if (cwx > 0.001) {
          if (kP > 0.001) CX = ${RHC(q)}(${CLIFF.layer}, vec2(gP.z, -gP.y) * iC, gwx * iC, gwy * iC, nbk,
            gmTan(gV, gNs, vec3(0.0, 0.0, 1.0), vec3(0.0, -1.0, 0.0)), gmTan(uSunL, gNs, vec3(0.0, 0.0, 1.0), vec3(0.0, -1.0, 0.0)), iC, kP, gOX);
          else CX = gmHex(${CLIFF.layer}, vec2(gP.z, -gP.y) * iC, gwx * iC, gwy * iC, nbk);
        }
        if (cwx < 0.999) {
          if (kP > 0.001) CZ = ${RHC(q)}(${CLIFF.layer}, vec2(gP.x, -gP.y) * iC, gzx * iC, gzy * iC, nbk,
            gmTan(gV, gNs, vec3(1.0, 0.0, 0.0), vec3(0.0, -1.0, 0.0)), gmTan(uSunL, gNs, vec3(1.0, 0.0, 0.0), vec3(0.0, -1.0, 0.0)), iC, kP, gOZ);
          else CZ = gmHex(${CLIFF.layer}, vec2(gP.x, -gP.y) * iC, gzx * iC, gzy * iC, nbk);
        }
        if (kP > 0.001) gHC = 1.0;
        gO = mix(gO, mix(gOZ, gOX, cwx), kC);
        gH = mix(gH, gHC, kC);` : `
        if (cwx > 0.001) CX = gmHex(${CLIFF.layer}, vec2(gP.z, -gP.y) * iC, gwx * iC, gwy * iC, nbk);
        if (cwx < 0.999) CZ = gmHex(${CLIFF.layer}, vec2(gP.x, -gP.y) * iC, gzx * iC, gzy * iC, nbk);`) + `
        Dn = mix(Dn, mix(CZ.a.rgb, CX.a.rgb, cwx) * 2.0, kC);
        cav = mix(cav, mix(CZ.c, CX.c, cwx) * 2.0, kC);
        dnCX = CX.n * uGmMat.w;
        dnCZ = CZ.n * uGmMat.w;
      }` : '') + `
      gD = mix(vec3(1.0), Dn, kN * uGmMat.z);
      // 5. relight: the bake's light model, detail normal over baked normal
      if (kR > 0.001) {
        vec3 Nb = vec3(nb.x, sqrt(max(0.0, 1.0 - dot(nb, nb))), nb.y);
        vec3 Nd = gmPerturb(Nb, vec3(1.0, 0.0, 0.0), vec3(0.0, 0.0, 1.0), dn);` + (q >= 2 ? `
        if (wall > 0.001) {
          vec3 Nw = gmPerturb(Nb, xf ? vec3(0.0, 0.0, 1.0) : vec3(1.0, 0.0, 0.0), vec3(0.0, -1.0, 0.0), dnW);
          Nd = normalize(mix(Nd, Nw, wall));
        }` : '') + (CL ? `
        if (kC > 0.001) {
          vec3 Nc = normalize(mix(gmPerturb(Nb, vec3(1.0, 0.0, 0.0), vec3(0.0, -1.0, 0.0), dnCZ),
                                  gmPerturb(Nb, vec3(0.0, 0.0, 1.0), vec3(0.0, -1.0, 0.0), dnCX), cwx));
          Nd = normalize(mix(Nd, Nc, kC));
        }` : '') + `
        vec3 f = clamp(gmE(Nd, ${RO ? 'vis * (1.0 - kP * gO)' : 'vis'}, cav) / max(gmE(Nb, vis, 1.0), vec3(1e-4)), ${RLO ? `mix(uGmMat2.x, uGmRoughF[${q}].z, gH * kP)` : 'uGmMat2.x'}, uGmMat2.y);`
          + (RO ? `   // S2: the self-shadow scales the sun term only; a rough layer's floor RLO` : '') + `
        gF = mix(vec3(1.0), f, kR);
      }
    }
    // 4. macro: the same layers at macro_k x the period
    if (kM > 0.001 && m1 != GM_NONE) {
      float gIM = GM_INVM[m1] * uGmMat.y;
      vec3 DM = ${KO ? `gmKD(${macroTap('m1')}, gK1)` : macroTap('m1')};` + (q >= 2 ? `
      if (m2 != GM_NONE && wb > 0.001) {
        float gIM1 = gIM;
        gIM = GM_INVM[min(m2, GM_NL - 1)] * uGmMat.y;
        DM = mix(DM, ${KO ? `gmKD(${macroTap('m2')}, gK2)` : macroTap('m2')}, wb);
        gIM = gIM1;
      }` : '') + (CL ? `
      if (kC > 0.001) {
        float gIC = uGmCliff.y;
        vec3 MX = vec3(1.0), MZ = vec3(1.0);
        if (cwx > 0.001) MX = textureGrad(uMatA, vec3(gMR * (vec2(gP.z, -gP.y) * gIC), ${f1(CLIFF.layer)}), gMR * (gwx * gIC), gMR * (gwy * gIC)).rgb * 2.0;
        if (cwx < 0.999) MZ = textureGrad(uMatA, vec3(gMR * (vec2(gP.x, -gP.y) * gIC), ${f1(CLIFF.layer)}), gMR * (gzx * gIC), gMR * (gzy * gIC)).rgb * 2.0;
        DM = mix(DM, mix(vec3(1.0), mix(MZ, MX, cwx), gmOn(${CLIFF.layer})), kC);
      }` : '') + `
      gD *= mix(vec3(1.0), DM, kM);
    }
    diffuseColor.rgb *= gD * gF * gWF;` + (q >= 1 && SNOW >= 0 ? `
    // snow: view-dependent crystal glints + a grazing sheen, dead by 160 m
    if (m1 == ${SNOW}) {
      float sfade = (1.0 - smoothstep(20.0, 160.0, vdD)) * gOn * gmOn(${SNOW}) * smoothstep(0.5, 0.8, w1 / max(w1 + max(w2, 0.0), 1e-5));
      if (sfade > 0.001) {
        vec2 hq = floor(gP.xz * 9.0);
        float h1s = fract(sin(dot(hq, vec2(127.1, 311.7))) * 43758.5453);
        vec3 vd = normalize(cameraPosition - gP);
        float tw = fract(h1s * 61.0 + vd.x * 4.0 + vd.z * 4.0 + vd.y * 2.0);
        float glint = step(0.86, h1s) * pow(1.0 - abs(tw - 0.5) * 2.0, 8.0);
        float sheen = pow(1.0 - clamp(vd.y, 0.0, 1.0), 3.0) * 0.10;
        diffuseColor.rgb += vec3((glint * 0.8 + sheen) * sfade);
      }
    }` : '') + (water ? waterTerrainGLSL('ground') : '') + `
  }`;
}

// ROCK-F C, streaks (header 7): the V_GROUND colour-tile tap, in place of
// three's map_fragment (null: three's own). Flat ground (and uMatOn 0) takes
// the very same texture2D as ever — bit for bit; a steep face re-taps with
// textureGrad: the fall line keeps its screen footprint, the contour's widens
// to what one texel spans down the fall line (max(texel, fall footprint) /
// cos(slope), x streak_k, capped at 1 / streak_cos_min). streak_taps 1: one
// tap, the anisotropic filter (terrain tiles: anisotropy 16) averages along
// the contour; > 1: that many taps spread along it, each the K-th of the
// footprint (for a device whose anisotropic filter is poor). The shader
// probe's separable-pattern rows prove the fall line keeps its contrast.
// Every derivative is taken before the branch.
export function mapGLSL(q, M = GM_MAP) {
  if (!STREAK_ON(q)) return null;
  const K = Math.max(1, Math.round(CFG.streak_taps));
  return `
${M.guard}
  vec4 sampledDiffuseColor = ${M.tap(M.uv)};
  {
    vec2 mUx = ${M.dx}, mUy = ${M.dy};
    vec3 sN = normalize(cross(dFdx(vWorldPos2), dFdy(vWorldPos2)));
    float sc = abs(sN.y);
    float ws = (1.0 - smoothstep(uGmSlope.z, uGmSlope.w, sc)) * uMatOn;
    // uv = (x, -z) / tile: the fall line (down the slope) and the contour
    vec2 fl = vec2(sN.x, -sN.z);
    float fl2 = dot(fl, fl);
    vec2 nf = fl2 > 1e-8 ? fl * inversesqrt(fl2) : vec2(1.0, 0.0);
    vec2 nc = vec2(-nf.y, nf.x);
    float ff = max(abs(dot(mUx, nf)), abs(dot(mUy, nf)));
    float fc = max(abs(dot(mUx, nc)), abs(dot(mUy, nc)));
    float tx = 1.0 / ${M.texels};
    float ext = max(fc, max(tx, ff) / max(sc, uGmCliff.z) * uGmCliff.w);
    if (ws > 0.0) {
      // the spread beyond the screen footprint, ramped in with the slope
` + (K > 1 ? `
      vec2 sp = nc * (ws * max(0.0, ext - fc));` : '') + `
      vec2 gA = mix(mUx, nf * ff, ws), gB = mix(mUy, nc * max(fc, ext / ${f1(K)}), ws);
` + (K === 1 ? `
      sampledDiffuseColor = ${M.grad(M.uv, 'gA', 'gB')};` : `
      vec4 acc = vec4(0.0);
      for (int k = 0; k < ${K}; k++) acc += ${M.grad(`${M.uv} + sp * ((float(k) + 0.5) / ${f1(K)} - 0.5)`, 'gA', 'gB')};
      sampledDiffuseColor = acc / ${f1(K)};`) + `
    }
  }
  diffuseColor *= sampledDiffuseColor;
#endif`;
}

// The V_MACRO block (far rings): coarse class, one macro tap. Same scope.
export function macroGLSL(water = false) {
  return `
  {
    vec3 gP = vWorldPos2;
    vec2 gdx = dFdx(gP.xz), gdy = dFdy(gP.xz);
    const mat2 gMR = mat2(0.8, 0.6, -0.6, 0.8);
    int ba, bb; float bf;
    gmBand(gP.y, ba, bb, bf);
    int c = gmCoarseAt(ivec2(floor(gP.xz / uClassInfo.x)));
    int m1 = c < 16 ? GM_CL[c] : (c == 255 ? (bf < 0.5 ? ba : bb) : GM_NONE);
    float kM = ${GM_LITE() ? '0.0' : 'uGmMat.x * uMatOn'};
    if (m1 != GM_NONE && kM > 0.001) {
      float gIM = GM_INVM[m1] * uGmMat.y;
      diffuseColor.rgb *= mix(vec3(1.0), ${macroTap('m1')}, kM);
    }${water ? waterTerrainGLSL('macro') : ''}
  }`;
}

// The tier-0 / no-material variants (terrain.js attachVariant): no ground
// block — the water term alone (needs waterDecl + bindGround).
export function bareWaterGLSL() {
  if (W_OFF()) return '';
  return `
  {
    vec3 gP = vWorldPos2;
    vec2 gdx = dFdx(gP.xz), gdy = dFdy(gP.xz);${waterTerrainGLSL('bare')}
  }`;
}
